import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Calendar, dateFnsLocalizer, Views, type View, type Components } from 'react-big-calendar';
import { format, parse, startOfWeek, endOfWeek, getDay, isSameDay, isWithinInterval, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isBefore, startOfDay } from 'date-fns';
import {
  DASHBOARD_HORIZON_WEEKS,
  DASHBOARD_NEAR_WEEKS,
  UPCOMING_TARGET,
  bufferedCalendarRange,
  contiguousCoverageEnd,
  countStartsInWindow,
  mergeById,
  mergeRanges,
  normalizeCalendarView,
  rangeAroundToday,
  uncoveredParts,
  type MsRange,
} from '../lib/appointmentWindow';
import { de } from 'date-fns/locale';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import AdminSettings from './AdminSettings';
import AdminTemplates from './AdminTemplates';
import InboxPopover from '../components/InboxPopover';
import BookingToastHost from '../components/BookingToastHost';
import ConsentCheckbox from '../components/ConsentCheckbox';
import { useAuth } from '../contexts/AuthContext';
import { useAppointmentNotifications } from '../hooks/useAppointmentNotifications';

// --- UTILS ---
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- ICONS ---
const Icons = {
  Grid: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1.5" /><rect width="7" height="7" x="14" y="3" rx="1.5" /><rect width="7" height="7" x="14" y="14" rx="1.5" /><rect width="7" height="7" x="3" y="14" rx="1.5" /></svg>,
  Calendar: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>,
  Users: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  LogOut: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>,
  Search: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>,
  Refresh: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>,
  ChevronLeft: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>,
  ChevronRight: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>,
  TrendingUp: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>,
  X: () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  User: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
  Settings: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>,
  Phone: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>,
  Clock: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
  MessageSquare: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
  Layout: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="9" x2="9" y2="21"/></svg>,
  Plus: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Check: () => <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Key: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></svg>,
  Copy: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>,
  Download: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  Zap: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Menu: () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  XCircle: () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
  Trash: () => <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>,
  ChevronDown: () => <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>,
  FileText: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
};

// --- DATENSCHUTZ-BAUSTEIN (Chatbot/Gemini) ---
// Textvorschlag zur Ergänzung der Datenschutzerklärung des Mandanten. Deckt die
// Art.-13-Pflichtangaben für den Chatbot ab (Zweck, Rechtsgrundlage, Empfänger/
// Unterauftragsverarbeiter Google Gemini inkl. Drittlandtransfer, Speicherdauer).
// Kein Ersatz für eine anwaltliche Prüfung im Einzelfall (siehe Disclaimer im UI).
// Platzhalter bewusst nicht aus userSettings vorbefüllt: der Mandant soll jede
// Klammer selbst ausfüllen, damit ein falscher/veralteter Account-Wert nicht
// unbemerkt in seine Datenschutzerklärung übernommen wird.
function buildPrivacyBaustein(): string {
  const firma = '[hier Firmennamen einfügen]';
  return `Chatbot- und KI-gestützte Terminbuchung

${firma} setzt auf seiner Website das Buchungs- und Chat-Widget „Candlear Booking" ein, bereitgestellt von:
Synergic Systems – Jannick Müller, Maria-Hilf-Straße 11, 50677 Köln, Deutschland, datenschutz@candlear.de

Candlear Booking handelt dabei als unser Auftragsverarbeiter gemäß Art. 28 DSGVO (Auftragsverarbeitungsvertrag einsehbar unter https://candlear.de/avv).

Verarbeitete Daten: Im Chat eingegebene Angaben wie Name, E-Mail-Adresse bzw. Telefonnummer sowie Terminwunsch (Datum, Uhrzeit, Terminart), außerdem der Chatverlauf.

Zweck und Rechtsgrundlage: Automatisierte Terminbuchung und Beantwortung von Anfragen im Chat; Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung bzw. vorvertragliche Maßnahmen auf Ihre Anfrage).

KI-gestützte Verarbeitung: Zur automatisierten Beantwortung von Chat-Anfragen wird der Chatverlauf an Google LLC (Gemini API) übermittelt und dort verarbeitet. Google verwendet Ihre Eingaben nicht zur Verbesserung eigener Produkte. Die Verarbeitung kann auch außerhalb der EU/des EWR erfolgen (weltweite Serverstandorte, keine Regionswahl möglich); die Übermittlung stützt sich auf die EU-Standardvertragsklauseln sowie das Data Processing Addendum for Products Where Google is a Data Processor. Weitere Infos: https://ai.google.dev/gemini-api/terms

Speicherdauer: Chat-Sitzungen werden 24 Stunden nach der letzten Interaktion automatisch gelöscht.

Ihre Rechte: Ihnen stehen die im Abschnitt [hier Abschnittsname/-nummer einfügen, z. B. „Ihre Rechte als betroffene Person"] dieser Datenschutzerklärung beschriebenen Rechte (u. a. Auskunft, Berichtigung, Löschung, Widerspruch) auch hinsichtlich der über den Chatbot verarbeiteten Daten zu.`;
}

// --- SETUP ---
const locales = { 'de': de };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

// --- CUSTOM CALENDAR TOOLBAR ---
const CustomToolbar = (props: any) => {
  const { date, onNavigate, view, onView } = props;
  const goToBack = () => onNavigate('PREV');
  const goToNext = () => onNavigate('NEXT');
  const goToToday = () => onNavigate('TODAY');

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 sm:px-5 py-3 sm:py-4 border-b border-[#E8EAF6] flex-shrink-0">
      {/* Left: prev / Heute / next / title */}
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        <button onClick={goToBack} aria-label="Zurück" className="w-9 h-9 sm:w-[30px] sm:h-[30px] flex-shrink-0 flex items-center justify-center rounded-[8px] hover:bg-[#F4F5FC] transition-colors text-[#654F6F] hover:text-[#0E0B1E]">
          <Icons.ChevronLeft />
        </button>
        <button onClick={goToToday} className="flex-shrink-0 px-3 h-9 sm:h-auto sm:py-[5px] border border-[#E8EAF6] bg-white text-[11px] font-bold text-[#0E0B1E] rounded-[9px] hover:border-[#FF6D00]/40 hover:text-[#FF6D00] transition-colors">
          Heute
        </button>
        <button onClick={goToNext} aria-label="Weiter" className="w-9 h-9 sm:w-[30px] sm:h-[30px] flex-shrink-0 flex items-center justify-center rounded-[8px] hover:bg-[#F4F5FC] transition-colors text-[#654F6F] hover:text-[#0E0B1E]">
          <Icons.ChevronRight />
        </button>
        <span className="text-[14px] font-bold text-[#0E0B1E] ml-1 whitespace-nowrap truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {format(date, 'MMMM yyyy', { locale: de })}
        </span>
      </div>
      {/* Right: view tabs */}
      <div className="flex bg-[#F4F5FC] p-[3px] rounded-[10px] gap-0.5 flex-shrink-0">
        {['month', 'week', 'day'].map((v) => (
          <button
            key={v}
            onClick={() => onView(v)}
            className={cn(
              "px-3 h-8 sm:h-auto sm:py-[5px] rounded-[7px] text-[11px] font-bold transition-colors",
              view === v
                ? "bg-white text-[#0E0B1E] shadow-sm"
                : "text-[#654F6F] hover:text-[#0E0B1E] hover:bg-white/70"
            )}
          >
            {v === 'week' ? 'Woche' : v === 'month' ? 'Monat' : 'Tag'}
          </button>
        ))}
      </div>
    </div>
  );
};

// --- HELPERS ---
function resolveAppointmentTypeLabel(typeId: string | null | undefined, types: any[]): string | null {
  if (!typeId || !types.length) return null;
  return types.find((t: any) => t.id === typeId || t.name === typeId)?.name ?? null;
}

function suggestionSlotKey(suggestion: { start?: string; resource?: string }, index?: number) {
  const start = suggestion.start || '';
  const resource = suggestion.resource || '';
  if (!start && !resource) return `slot-${index ?? 0}`;
  return `${start}::${resource || String(index ?? '')}`;
}

function resolveResourceLabel(resourceId: string | null | undefined, resources: any[]): string {
  if (!resourceId) return '';
  const found = resources.find((r: any) => r.id === resourceId);
  return found?.name || resourceId;
}

// --- CUSTOM EVENT ---
// event._justChanged / event._fading sind transiente Flags, die nur für den einen
// Termin gesetzt werden, der gerade live (ohne Reload) dazukam oder storniert wurde —
// siehe useLiveAppointmentUpdates weiter unten. Alle anderen Termine bleiben unberührt.
const CustomEvent = ({ event, appointmentTypes = [] }: any) => {
  const resourceColor = event.color || event.extendedProps?.resourceColor || '#FF6D00';
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
      : { r: 59, g: 130, b: 246 };
  };
  const rgb = hexToRgb(resourceColor);

  const clientName = event.title.split('(')[0].trim();
  const startTime = event.start ? format(new Date(event.start), 'HH:mm') : '';
  const endTime = event.end ? format(new Date(event.end), 'HH:mm') : '';
  const timeRange = startTime && endTime ? `${startTime}–${endTime}` : startTime;
  const typeLabel = resolveAppointmentTypeLabel(event.appointmentTypeId, appointmentTypes);
  const isNew = !!event._justChanged;
  const isFading = !!event._fading;
  // Raster hat 1px pro Minute: kurze Termine bekommen weniger Zeilen, statt dass sie überlaufen
  const durationMin = event.start && event.end
    ? (new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000
    : 60;
  const layout: 'line' | 'two' | 'full' = durationMin < 45 ? 'line' : durationMin < 70 ? 'two' : 'full';
  const tooltipText = [clientName, timeRange, typeLabel].filter(Boolean).join(' · ');
  const nameStyle = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
  const metaStyle = { color: 'rgba(255,255,255,0.82)', fontFamily: "'Inter', sans-serif" };

  return (
    <motion.div
      title={tooltipText}
      initial={isNew ? { opacity: 0, scale: 0.82 } : false}
      animate={isFading ? { opacity: 0, scale: 0.86 } : { opacity: 1, scale: 1 }}
      transition={isFading ? { duration: 0.22, ease: 'easeIn' } : { duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className="h-full w-full flex flex-col justify-start relative overflow-hidden"
      style={{
        backgroundColor: `rgba(${rgb.r},${rgb.g},${rgb.b},0.92)`,
        borderRadius: '6px',
        boxShadow: isNew
          ? `0 0 0 2px #FF6D00, 0 4px 14px rgba(${rgb.r},${rgb.g},${rgb.b},0.35)`
          : `0 2px 6px rgba(${rgb.r},${rgb.g},${rgb.b},0.30)`,
        padding: layout === 'line' ? '1px 7px' : '3px 7px 2px',
        transition: 'box-shadow 480ms ease',
      }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, transparent 55%)' }} />
      {layout === 'line' ? (
        <span className="text-[11px] leading-[14px] truncate text-white relative">
          <span className="font-bold" style={nameStyle}>{clientName}</span>
          {timeRange && <span className="text-[10px] ml-1.5" style={metaStyle}>{timeRange}</span>}
          {typeLabel && <span className="text-[10px] italic" style={{ ...metaStyle, color: 'rgba(255,255,255,0.70)' }}> · {typeLabel}</span>}
        </span>
      ) : layout === 'two' ? (
        <>
          <span className="text-[11px] font-bold leading-tight truncate text-white relative" style={nameStyle}>
            {clientName}
          </span>
          {(timeRange || typeLabel) && (
            <span className="text-[10px] leading-tight truncate relative mt-0.5" style={metaStyle}>
              {timeRange}
              {typeLabel && <span className="italic" style={{ color: 'rgba(255,255,255,0.70)' }}>{timeRange ? ' · ' : ''}{typeLabel}</span>}
            </span>
          )}
        </>
      ) : (
        <>
          <span
            className="text-[11px] font-bold leading-tight truncate text-white relative"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {clientName}
          </span>
          {timeRange && (
            <span
              className="text-[10px] leading-tight truncate relative mt-0.5"
              style={{ color: 'rgba(255,255,255,0.82)', fontFamily: "'Inter', sans-serif" }}
            >
              {timeRange}
            </span>
          )}
          {typeLabel && (
            <span
              className="text-[9px] leading-tight truncate relative mt-0.5"
              style={{ color: 'rgba(255,255,255,0.70)', fontFamily: "'Inter', sans-serif", fontStyle: 'italic' }}
            >
              {typeLabel}
            </span>
          )}
        </>
      )}
    </motion.div>
  );
};

// --- MONTH VIEW WEEKDAY NAMES (just "Mo", "Di" etc.) ---
const MonthWeekdayHeader = ({ label }: any) => (
  <div style={{
    padding: '8px 0', textAlign: 'center',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: '#654F6F',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  }}>{label}</div>
);

// --- MONTH VIEW DATE CELL (day number with today-pill) ---
const CustomMonthDateCell = ({ date }: any) => {
  const today = isSameDay(date, new Date());
  const dayNum = format(date, 'd');
  return (
    <div style={{ textAlign: 'right', padding: '4px 8px 2px' }}>
      {today ? (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: '50%',
          background: '#FF6D00', color: 'white', fontSize: 12, fontWeight: 700,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          boxShadow: '0 2px 8px rgba(255,109,0,0.35)',
        }}>{dayNum}</span>
      ) : (
        <span style={{
          fontSize: 12, fontWeight: 600, color: '#374151',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>{dayNum}</span>
      )}
    </div>
  );
};

// --- MONTH VIEW EVENT (compact) ---
const CustomMonthEvent = ({ event, appointmentTypes = [] }: any) => {
  const color = event.color || '#FF6D00';
  const clientName = event.title.split('(')[0].trim();
  const startTime = event.start ? format(new Date(event.start), 'HH:mm') : '';
  const typeLabel = resolveAppointmentTypeLabel(event.appointmentTypeId, appointmentTypes);
  const tooltipText = [clientName, typeLabel].filter(Boolean).join(' · ');
  const isNew = !!event._justChanged;
  const isFading = !!event._fading;
  return (
    <motion.div
      title={tooltipText}
      initial={isNew ? { opacity: 0, scale: 0.8 } : false}
      animate={isFading ? { opacity: 0, scale: 0.86 } : { opacity: 1, scale: 1 }}
      transition={isFading ? { duration: 0.22, ease: 'easeIn' } : { duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: color,
        borderRadius: 4,
        padding: '1px 6px',
        fontSize: 10,
        fontWeight: 700,
        color: 'white',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        lineHeight: '18px',
        boxShadow: isNew ? '0 0 0 2px #FF6D00' : undefined,
        height: '100%',
      }}>
      {startTime && <span style={{ opacity: 0.85, marginRight: 3 }}>{startTime}</span>}
      {clientName}
      {typeLabel && <span style={{ opacity: 0.75, marginLeft: 4, fontWeight: 400 }}>· {typeLabel}</span>}
    </motion.div>
  );
};

// --- WEEK DAY HEADER (shows day abbr + date number) ---
// NOTE: must use only <span> elements — rbc wraps this in <button> (phrasing content only)
const CustomWeekHeader = ({ date }: { date: Date }) => {
  const today = isSameDay(date, new Date());
  const dayOfWeek = getDay(date);
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const dayAbbr = format(date, 'EEE', { locale: de }).slice(0, 2).toUpperCase();
  const dayNum = format(date, 'd');
  return (
    <span style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '10px 4px',
      opacity: isWeekend ? 0.4 : 1, boxSizing: 'border-box',
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
        color: today ? '#FF6D00' : '#654F6F', lineHeight: '14px',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>{dayAbbr}</span>
      {today ? (
        <span style={{
          width: 30, height: 30, borderRadius: '50%',
          background: '#FF6D00', color: 'white',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginTop: 3, fontSize: 14, fontWeight: 700,
          boxShadow: '0 4px 10px rgba(255,109,0,0.35)',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>{dayNum}</span>
      ) : (
        <span style={{
          fontSize: 17, fontWeight: 700, color: '#0E0B1E',
          marginTop: 3, lineHeight: '22px',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>{dayNum}</span>
      )}
    </span>
  );
};

// --- RESOURCE HEADER ---
const ResourceHeader = ({ label, resource }: any) => {
  const color = resource?.color || '#654F6F';
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (
    <div className="flex flex-col items-center justify-end pb-3 pt-4 px-2 relative overflow-hidden" style={{ minHeight: '80px' }}>
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: color }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at 50% 0%, rgba(${r},${g},${b},0.10) 0%, transparent 70%)` }} />
      <div
        className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[11px] font-black mb-1.5 relative"
        style={{ backgroundColor: color, boxShadow: `0 4px 14px rgba(${r},${g},${b},0.40)`, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {(label || '?').charAt(0).toUpperCase()}
      </div>
      <span
        className="text-[10px] font-bold uppercase tracking-widest truncate max-w-[90px] text-center relative"
        style={{ color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {label}
      </span>
      {typeof resource?.capacity === 'number' && (
        <span className="text-[9px] font-semibold relative mt-0.5" style={{ color: '#654F6F' }}>
          {resource.capacity} Pers.
        </span>
      )}
    </div>
  );
};

// --- API ---
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/** Fallback, falls der Server noch kein api_active liefert; maßgeblich ist isSubscriptionActive in functions/index.js */
function isStripeSubscriptionActiveForApi(status: string | null | undefined): boolean {
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

const WORDPRESS_PLUGIN_ZIP_URL = '/downloads/candlear-booking-widget.zip';

function copyTextToClipboard(text: string, successMessage: string) {
  navigator.clipboard.writeText(text)
    .then(() => alert(successMessage))
    .catch((err) => {
      console.error('[WIDGET] Kopieren in die Zwischenablage fehlgeschlagen', err);
      alert('Kopieren fehlgeschlagen – bitte manuell markieren und kopieren.');
    });
}

// --- FORGOT PASSWORD MODAL ---
function ForgotPasswordModal({ onClose }: { onClose: () => void }) {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    try {
      await resetPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (error: any) {
      setErr('E-Mail konnte nicht gesendet werden. Bitte prüfe die Adresse.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="bg-white w-full max-w-sm rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.3)] relative z-10 p-6 sm:p-8 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
        {sent ? (
          <div className="text-center space-y-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Icons.Check />
            </div>
            <p className="font-semibold text-[#0E0B1E]">E-Mail gesendet</p>
            <p className="text-sm text-gray-500">Prüfe deinen Posteingang für den Reset-Link.</p>
            <button onClick={onClose} className="w-full mt-2 bg-[#FF6D00] text-white font-bold text-sm py-2.5 rounded-xl">Schließen</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="font-bold text-[#0E0B1E] text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Passwort zurücksetzen</h3>
            <p className="text-sm text-gray-500">Gib deine E-Mail-Adresse ein. Wir senden dir einen Reset-Link.</p>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="deine@email.de" className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/15 focus:border-[#FF6D00] transition-all" />
            {err && <p className="text-red-500 text-xs">{err}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Abbrechen</button>
              <button type="submit" className="flex-1 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white font-bold text-sm py-2.5 rounded-xl">Senden</button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, signIn, signInWithGoogle, signUp, signOut: authSignOut, getAuthHeader } = useAuth();
  const inbox = useAppointmentNotifications();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [registerData, setRegisterData] = useState({ email: '', password: '', confirmPassword: '', business_name: '' });
  const [avvAccepted, setAvvAccepted] = useState(false);

  // Google-Erstregistrierung: Firebase meldet den Nutzer an, bevor wir wissen, ob es
  // schon ein Konto gibt. Existiert keines, antwortet /admin/setup mit AVV_REQUIRED —
  // dann pausiert der Login hier, bis die Zustimmung aktiv bestätigt wurde (CAN-33).
  const [pendingConsent, setPendingConsent] = useState(false);
  // Wird SYNCHRON vor dem Google-Sign-in gesetzt. Ohne dieses Flag klafft eine Lücke:
  // Nach dem Sign-in ist `user` sofort da und das Security-Gate für Google direkt auf
  // 'ok', also feuern die Lade-Effekte, während der /admin/setup-Aufruf noch läuft und
  // pendingConsent noch false ist. Genau dadurch entstand ein Konto ohne Zustimmung.
  const [setupInFlight, setSetupInFlight] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState('');

  // Security-Gate: Passwort-Logins müssen E-Mail verifizieren und 2FA bestehen,
  // bevor das Dashboard lädt. Google-Logins und Testkonten (Claim test_account) sind ausgenommen.
  const [securityGate, setSecurityGate] = useState<'checking' | 'mfa' | 'ok'>('checking');
  const [gateBusy, setGateBusy] = useState(false);
  const [gateMessage, setGateMessage] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const mfaCodeRequestedRef = useRef(false);
  const [events, setEvents] = useState<any[]>([]);
  const [loadedRanges, setLoadedRanges] = useState<MsRange[]>([]);
  const coveredRef = useRef<MsRange[]>([]);
  const pendingRef = useRef<MsRange[]>([]);
  const eventsRef = useRef<any[]>([]);
  const resourcesCacheRef = useRef<any[]>([]);
  const resourcesLoadedRef = useRef(false);
  const appointmentQueueRef = useRef<Promise<void>>(Promise.resolve());
  const appointmentFetchGenRef = useRef(0);
  const syncAppointmentsRef = useRef<(mode: 'ensure' | 'refresh') => Promise<void>>(async () => {});
  // IDs von Terminen, die gerade live (ohne Reload) neu dazugekommen bzw. storniert wurden —
  // nur diese eine Karte im Kalender bekommt kurz eine Animation, siehe filteredEvents unten.
  const [justBookedEventIds, setJustBookedEventIds] = useState<Set<string>>(new Set());
  const [fadingOutEventIds, setFadingOutEventIds] = useState<Set<string>>(new Set());
  const [userSettings, setUserSettings] = useState<any | null>(null);
  const [botConfig, setBotConfig] = useState<any | null>(null);
  const [clients, setClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);

  const [resourceFilter, setResourceFilter] = useState('all');
  const [fetchedResources, setFetchedResources] = useState<any[]>([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState<View>(Views.WEEK);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'calendar' | 'clients' | 'chatbot' | 'templates' | 'settings'>('dashboard');
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [todayFilterOpen, setTodayFilterOpen] = useState(false);
  const todayFilterRef = useRef<HTMLDivElement>(null);
  const [auslastungOpen, setAuslastungOpen] = useState<boolean | null>(null);
  const [privacyBausteinOpen, setPrivacyBausteinOpen] = useState(false);
  const [privacyBausteinText, setPrivacyBausteinText] = useState<string | null>(null);

  const [checkoutBanner, setCheckoutBanner] = useState<'success' | 'cancel' | null>(null);

  const [chatStripeLoadState, setChatStripeLoadState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [chatApiActive, setChatApiActive] = useState(false);

  const [quickBookOpen, setQuickBookOpen] = useState(false);
  const [quickBookStep, setQuickBookStep] = useState<1|2|3>(1);
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());

  const roundToHalfHour = () => {
    const now = new Date();
    const m = now.getMinutes();
    const addHour = m >= 30;
    const h = addHour ? now.getHours() + 1 : now.getHours();
    const mins = addHour ? '00' : '30';
    return `${String(h % 24).padStart(2, '0')}:${mins}`;
  };

  const [quickBookForm, setQuickBookForm] = useState({
    date: '', time: roundToHalfHour(), clientName: '', contact: '', resource: '', appointmentType: '', gapAware: true, selectedStart: '', selectedEnd: '', selectedResource: '',
  });
  const [quickBookSuggestions, setQuickBookSuggestions] = useState<any[]>([]);
  const [quickBookLoading, setQuickBookLoading] = useState(false);
  const [quickBookSubmitting, setQuickBookSubmitting] = useState(false);
  const [quickBookError, setQuickBookError] = useState('');
  const suggestDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always holds the latest quickBookForm so event handlers never read stale closure values.
  const quickBookFormRef = useRef(quickBookForm);
  quickBookFormRef.current = quickBookForm;

  // --- LOGIC ---

  // Prüft, welche Sicherheitsstufe die aktuelle Sitzung erreicht hat.
  // forceRefresh holt ein frisches Token (nötig nach Verifizierung/2FA, damit
  // email_verified und mfa_ok_until im Token ankommen).
  const evaluateSecurityGate = async (forceRefresh = false) => {
    if (!user) return;
    const isPasswordLogin = user.providerData.some(p => p.providerId === 'password');
    if (!isPasswordLogin) { setSecurityGate('ok'); return; }
    try {
      const tokenResult = await user.getIdTokenResult(forceRefresh);
      if (tokenResult.claims.test_account === true) { setSecurityGate('ok'); return; }
      const mfaOkUntil = Number(tokenResult.claims.mfa_ok_until ?? 0);
      setSecurityGate(mfaOkUntil > Date.now() ? 'ok' : 'mfa');
    } catch (err) {
      console.error('[AUTH] Security-Gate-Prüfung fehlgeschlagen:', err);
      setSecurityGate('mfa');
    }
  };

  useEffect(() => {
    if (!user) { setSecurityGate('checking'); mfaCodeRequestedRef.current = false; return; }
    setSecurityGate('checking');
    setGateMessage('');
    setMfaCode('');
    // forceRefresh: frisch gesetzte Claims (z. B. test_account) sofort übernehmen
    evaluateSecurityGate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Beim Eintritt in den MFA-Schritt automatisch einen Code anfordern — aber
  // nicht erneut, wenn in diesem Browser in den letzten 2 Minuten schon einer
  // gesendet wurde (zweiter Tab, Reload, React Strict Mode).
  useEffect(() => {
    if (securityGate !== 'mfa' || !user || mfaCodeRequestedRef.current) return;
    mfaCodeRequestedRef.current = true;
    const lastSent = Number(localStorage.getItem(`candlear_mfa_sent_${user.uid}`) || 0);
    if (lastSent && Date.now() - lastSent < 2 * 60 * 1000) {
      setGateMessage('Wir haben dir einen 6-stelligen Code per E-Mail gesendet.');
      return;
    }
    requestMfaCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityGate]);

  // Nach frischer Registrierung: sobald das Gate passiert ist, Abo-Checkout starten
  useEffect(() => {
    if (!user || securityGate !== 'ok') return;
    if (sessionStorage.getItem('candlear_post_register') !== '1') return;
    (async () => {
      try {
        const headers = await getAuthHeader();
        const setupRes = await axios.post(`${API_BASE_URL}/admin/setup`, {}, { headers });
        sessionStorage.removeItem('candlear_post_register');
        if (!setupRes.data.subscription_status) {
          const checkoutRes = await axios.post(`${API_BASE_URL}/admin/stripe/create-checkout-session`, {}, { headers });
          window.location.href = checkoutRes.data.url;
        }
      } catch (err) {
        console.error('[AUTH] Checkout nach Registrierung fehlgeschlagen:', err);
        sessionStorage.removeItem('candlear_post_register');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, securityGate]);

  const requestMfaCode = async () => {
    setGateBusy(true);
    setGateMessage('');
    try {
      const headers = await getAuthHeader();
      await axios.post(`${API_BASE_URL}/admin/mfa/request-code`, {}, { headers });
      if (user) localStorage.setItem(`candlear_mfa_sent_${user.uid}`, String(Date.now()));
      setGateMessage('Wir haben dir einen 6-stelligen Code per E-Mail gesendet.');
    } catch (err: any) {
      console.error('[MFA] Code-Anforderung fehlgeschlagen:', err);
      setGateMessage(err.response?.data?.message || 'Code konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      setGateBusy(false);
    }
  };

  const handleMfaVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setGateBusy(true);
    setGateMessage('');
    try {
      const headers = await getAuthHeader();
      await axios.post(`${API_BASE_URL}/admin/mfa/verify`, { code: mfaCode.trim() }, { headers });
      await user.reload();
      await user.getIdToken(true); // Token mit email_verified + mfa_ok_until holen
      await evaluateSecurityGate(true);
    } catch (err: any) {
      console.error('[MFA] Code-Prüfung fehlgeschlagen:', err);
      setGateMessage(err.response?.data?.message || 'Code-Prüfung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setGateBusy(false);
    }
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await signIn(loginEmail.trim().toLowerCase(), loginPassword);
    } catch (err: any) {
      console.error('[AUTH] Login error:', err);
      const code = err.code as string;
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        setLoginError('E-Mail oder Passwort ist falsch.');
      } else if (code === 'auth/too-many-requests') {
        setLoginError('Zu viele Versuche. Bitte warte kurz und versuche es erneut.');
      } else {
        setLoginError(`Anmeldefehler: ${err.message || 'Unbekannter Fehler'}`);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  /**
   * Setup abschließen und ggf. in den Stripe-Checkout weiterleiten.
   * Gemeinsam genutzt vom Google-Login (bestehendes Konto) und von der
   * Google-Erstregistrierung nach bestätigter Zustimmung.
   */
  const finishGoogleSetup = async (setupData: any, token: string) => {
    // Neue Nutzer ohne Abo → Stripe Checkout starten
    if (!setupData.subscription_status) {
      const checkoutRes = await axios.post(`${API_BASE_URL}/admin/stripe/create-checkout-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = checkoutRes.data.url;
      return;
    }
    await fetchUserSettings();
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    setLoginLoading(true);
    // Vor dem ersten await: sperrt die Lade-Effekte, bis feststeht, ob ein Konto existiert
    setSetupInFlight(true);
    try {
      const firebaseUser = await signInWithGoogle();
      const token = await firebaseUser.getIdToken();
      // Bewusst OHNE avv_accepted: /admin/setup ist idempotent und antwortet für ein
      // bestehendes Konto sofort mit Erfolg. Nur wenn ein Konto neu angelegt werden
      // müsste, kommt AVV_REQUIRED zurück — genau das ist unser Signal, dass hier
      // eine Erstregistrierung läuft und eine echte Zustimmung eingeholt werden muss.
      let setupRes;
      try {
        setupRes = await axios.post(`${API_BASE_URL}/admin/setup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } catch (setupErr: any) {
        if (setupErr.response?.data?.error === 'AVV_REQUIRED') {
          setConsentAccepted(false);
          setConsentError('');
          setPendingConsent(true);
          return;
        }
        throw setupErr;
      }
      await finishGoogleSetup(setupRes.data, token);
    } catch (err: any) {
      console.error('[AUTH] Google login error:', err);
      if (err.code !== 'auth/popup-closed-by-user') {
        setLoginError(`Google-Anmeldung fehlgeschlagen: ${err.message || 'Unbekannter Fehler'}`);
      }
    } finally {
      setLoginLoading(false);
      // pendingConsent ist zu diesem Zeitpunkt bereits gesetzt, falls Zustimmung nötig ist —
      // die Effekte bleiben also weiterhin gesperrt.
      setSetupInFlight(false);
    }
  };

  /** Google-Erstregistrierung nach bestätigter Zustimmung abschließen */
  const handleConsentConfirm = async () => {
    if (!consentAccepted) return;
    setConsentBusy(true);
    setConsentError('');
    try {
      const headers = await getAuthHeader();
      const setupRes = await axios.post(`${API_BASE_URL}/admin/setup`, { avv_accepted: true }, { headers });
      const token = (headers.Authorization || '').replace('Bearer ', '');
      setPendingConsent(false);
      await finishGoogleSetup(setupRes.data, token);
    } catch (err: any) {
      console.error('[AUTH] Consent setup error:', err);
      setConsentError(err.response?.data?.message || 'Konto konnte nicht erstellt werden. Bitte versuche es erneut.');
    } finally {
      setConsentBusy(false);
    }
  };

  /**
   * Abbruch: abmelden, damit kein angemeldeter Firebase-Nutzer ohne Firestore-Dokument
   * zurückbleibt. Das Firebase-Auth-Konto selbst bleibt bestehen — beim nächsten Login
   * greift dieselbe Zustimmungsabfrage erneut.
   */
  const handleConsentCancel = async () => {
    setPendingConsent(false);
    setConsentAccepted(false);
    setConsentError('');
    await authSignOut();
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) { alert("Passwörter stimmen nicht überein!"); return; }
    const pw = registerData.password;
    if (pw.length < 8 || !/[a-z]/.test(pw) || !/[A-Z]/.test(pw) || !/\d/.test(pw)) {
      alert("Passwort muss mindestens 8 Zeichen lang sein und Groß-, Kleinbuchstaben sowie eine Zahl enthalten.");
      return;
    }
    try {
      const firebaseUser = await signUp(registerData.email.trim().toLowerCase(), registerData.password);
      const token = await firebaseUser.getIdToken();
      await axios.post(`${API_BASE_URL}/admin/setup`, {
        business_name: registerData.business_name || undefined,
        avv_accepted: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Merker für den Checkout: startet, sobald E-Mail-Verifizierung + 2FA bestanden sind
      sessionStorage.setItem('candlear_post_register', '1');
      setShowRegisterModal(false);
      // Kein direkter Checkout mehr: erst E-Mail bestätigen, dann 2FA (Security-Gate übernimmt)
    } catch (err: any) {
      console.error('[AUTH] Registration error:', err);
      const code = err.code as string;
      if (code === 'auth/email-already-in-use') {
        alert('Diese E-Mail-Adresse ist bereits registriert.');
      } else {
        alert(err.response?.data?.error || `Fehler bei der Registrierung: ${err.message || 'Unbekannter Fehler'}`);
      }
    }
  };

  const invalidateAppointments = () => {
    appointmentFetchGenRef.current += 1;
    coveredRef.current = [];
    pendingRef.current = [];
    eventsRef.current = [];
    setLoadedRanges([]);
    setEvents([]);
  };

  const handleLogout = async () => {
    await authSignOut();
    resourcesLoadedRef.current = false;
    resourcesCacheRef.current = [];
    setFetchedResources([]);
    invalidateAppointments();
  };

  const handleTabChange = (tab: 'dashboard' | 'calendar' | 'clients' | 'chatbot' | 'templates' | 'settings') => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  const isCurrentFetch = (gen: number) => appointmentFetchGenRef.current === gen;

  const commitEvents = (next: any[], gen: number) => {
    if (!isCurrentFetch(gen)) return;
    eventsRef.current = next;
    setEvents(next);
  };

  const commitCovered = (next: MsRange[], gen: number) => {
    if (!isCurrentFetch(gen)) return;
    const merged = mergeRanges(next);
    coveredRef.current = merged;
    setLoadedRanges(merged);
  };

  const knownRanges = () => mergeRanges([...coveredRef.current, ...pendingRef.current]);

  const addPending = (parts: MsRange[]) => {
    pendingRef.current = [...pendingRef.current, ...parts];
  };

  const removePending = (parts: MsRange[]) => {
    pendingRef.current = pendingRef.current.filter(
      (pending) => !parts.some((part) => part.start === pending.start && part.end === pending.end)
    );
  };

  const mapAppointmentEvents = (data: any[], resources: any[]) => {
    const resourcesMap = new Map(resources.map((resource) => [resource.id, resource]));
    return data.map((ev: any) => {
      const resourceId = ev.extendedProps?.resourceId || ev.resourceId;
      const resourceDetails = resourcesMap.get(resourceId);
      const resourceColor = resourceDetails?.color || '#3B82F6';
      const resourceName = resourceDetails?.name || resourceId;
      return {
        id: ev.id, title: ev.title, start: new Date(ev.start), end: new Date(ev.end),
        resourceId,
        appointmentTypeId: ev.extendedProps?.appointment_type || null,
        extendedProps: { ...ev.extendedProps, resourceId, resourceName, resourceColor, patient_contact: ev.extendedProps?.patient_contact },
        status: ev.status, contact: ev.extendedProps?.patient_contact, color: resourceColor
      };
    });
  };

  const loadResources = async (authHeader: { Authorization: string }, force: boolean) => {
    if (resourcesLoadedRef.current && !force) return resourcesCacheRef.current;
    const resourcesRes = await axios.get(`${API_BASE_URL}/admin/resources`, { headers: authHeader });
    const resourcesList = resourcesRes.data.resources || [];
    resourcesCacheRef.current = resourcesList;
    resourcesLoadedRef.current = true;
    setFetchedResources(resourcesList);
    return resourcesList;
  };

  const fetchAppointmentWindow = async (
    range: MsRange,
    authHeader: { Authorization: string },
    resources: any[]
  ) => {
    console.log('[DASHBOARD] Loading appointments', {
      start: new Date(range.start).toISOString(),
      end: new Date(range.end).toISOString(),
    });
    const res = await axios.get(`${API_BASE_URL}/admin/events`, {
      params: { start: new Date(range.start).toISOString(), end: new Date(range.end).toISOString() },
      headers: authHeader,
    });
    return mapAppointmentEvents(res.data || [], resources);
  };

  const reportAppointmentError = (err: any) => {
    console.error('[DASHBOARD] Error fetching events:', err);
    const gateError = err.response?.data?.error === 'EMAIL_NOT_VERIFIED' || err.response?.data?.error === 'MFA_REQUIRED';
    if (axios.isAxiosError(err) && err.response?.status === 403 && !gateError) handleLogout();
  };

  // Fehlende Stücke holen und an den Cache hängen. Schon geladene Bereiche bleiben stehen,
  // auch wenn die Antwort leer ist — sonst wirkt ein leerer Monat wie eine noch nicht geladene Lücke.
  const loadMissing = async (
    range: MsRange,
    gen: number,
    authHeader: { Authorization: string },
    resources: any[]
  ) => {
    const missing = uncoveredParts(knownRanges(), range);
    if (missing.length === 0) return;
    addPending(missing);
    try {
      const batches = await Promise.all(
        missing.map((part) => fetchAppointmentWindow(part, authHeader, resources))
      );
      removePending(missing);
      if (!isCurrentFetch(gen)) return;
      commitEvents(mergeById(eventsRef.current, batches.flat()), gen);
      commitCovered([...coveredRef.current, ...missing], gen);
    } catch (err) {
      removePending(missing);
      throw err;
    }
  };

  const runAppointmentSync = async (
    mode: 'ensure' | 'refresh',
    request: { surface: 'dashboard'; now: Date } | { surface: 'calendar'; view: 'month' | 'week' | 'day'; cursor: Date }
  ) => {
    if (!user) return;
    const gen = appointmentFetchGenRef.current;
    try {
      const authHeader = await getAuthHeader();
      if (!isCurrentFetch(gen)) return;
      const resources = await loadResources(authHeader, mode === 'refresh');
      if (!isCurrentFetch(gen)) return;

      if (request.surface === 'calendar') {
        const range = bufferedCalendarRange(request.view, request.cursor);
        if (mode === 'refresh') {
          const fresh = await fetchAppointmentWindow(range, authHeader, resources);
          if (!isCurrentFetch(gen)) return;
          commitEvents(fresh, gen);
          commitCovered([range], gen);
          return;
        }
        await loadMissing(range, gen, authHeader, resources);
        return;
      }

      const now = request.now;
      const near = rangeAroundToday(now, DASHBOARD_NEAR_WEEKS);
      if (mode === 'refresh') {
        const fresh = await fetchAppointmentWindow(near, authHeader, resources);
        if (!isCurrentFetch(gen)) return;
        commitEvents(fresh, gen);
        commitCovered([near], gen);
      } else {
        await loadMissing(near, gen, authHeader, resources);
      }
      if (!isCurrentFetch(gen)) return;

      const upcoming = countStartsInWindow(
        eventsRef.current.map((event) => event.start.getTime()),
        now.getTime(),
        near
      );
      if (upcoming < UPCOMING_TARGET) {
        await loadMissing(rangeAroundToday(now, DASHBOARD_HORIZON_WEEKS), gen, authHeader, resources);
      }
    } catch (err: any) {
      reportAppointmentError(err);
    }
  };

  const syncAppointments = (mode: 'ensure' | 'refresh'): Promise<void> => {
    const request = activeTab === 'calendar'
      ? { surface: 'calendar' as const, view: normalizeCalendarView(view), cursor: date }
      : { surface: 'dashboard' as const, now: new Date() };
    if (mode === 'ensure' && activeTab !== 'dashboard' && activeTab !== 'calendar') {
      return Promise.resolve();
    }

    const task = appointmentQueueRef.current.then(
      () => runAppointmentSync(mode, request),
      () => runAppointmentSync(mode, request)
    );
    appointmentQueueRef.current = task.then(() => undefined, () => undefined);
    return task;
  };

  useEffect(() => {
    syncAppointmentsRef.current = syncAppointments;
  });

  const fetchUserSettings = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/settings`, { headers: await getAuthHeader() });
      setUserSettings(res.data);
    } catch (err) { console.error('[DASHBOARD] Failed to fetch user settings', err); }
  };

  const fetchBotConfig = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/bot-config`, { headers: await getAuthHeader() });
      setBotConfig(res.data);
    } catch (err) { console.error('[DASHBOARD] Failed to fetch bot config', err); }
  };

  const saveBotConfig = async (config: any) => {
    if (!user) return;
    try {
      await axios.put(`${API_BASE_URL}/admin/bot-config`, config, { headers: await getAuthHeader() });
      setBotConfig(config);
      alert('Bot-Konfiguration gespeichert!');
    } catch (err) {
      console.error('[DASHBOARD] Failed to save bot config', err);
      alert('Fehler beim Speichern der Bot-Konfiguration');
    }
  };

  const fetchClients = async () => {
    if (!user) return;
    setClientsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/clients`, { headers: await getAuthHeader() });
      setClients(res.data || []);
    } catch (err) {
      console.error('[DASHBOARD] Failed to fetch clients', err);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleDeleteClient = async (client: { id: string; name: string; appointment_count: number }) => {
    const confirmed = window.confirm(
      `Kunde "${client.name}" wirklich löschen?\n\nKunde und alle zugehörigen Termine (${client.appointment_count}) werden unwiderruflich gelöscht. Dies kann nicht rückgängig gemacht werden.`
    );
    if (!confirmed) return;

    setDeletingClientId(client.id);
    try {
      await axios.delete(`${API_BASE_URL}/admin/clients/${encodeURIComponent(client.id)}`, { headers: await getAuthHeader() });
      invalidateAppointments();
      await fetchClients();
    } catch (err) {
      console.error('[DASHBOARD] Failed to delete client', err);
      alert('Fehler beim Löschen des Kunden.');
    } finally {
      setDeletingClientId(null);
    }
  };

  useEffect(() => {
    if (!accountMenuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!todayFilterOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!todayFilterRef.current?.contains(event.target as Node)) {
        setTodayFilterOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTodayFilterOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [todayFilterOpen]);

  useEffect(() => {
    // pendingConsent / setupInFlight: Konto existiert (noch) nicht — Laden würde nur 404s
    // erzeugen und hat früher sogar ein Konto ohne Zustimmung angelegt
    if (user && securityGate === 'ok' && !pendingConsent && !setupInFlight) {
      fetchUserSettings();
      if (activeTab === 'chatbot') fetchBotConfig();
      if (activeTab === 'clients') fetchClients();
    }
  }, [user, activeTab, securityGate, pendingConsent, setupInFlight]);

  useEffect(() => {
    if (!user || securityGate !== 'ok' || pendingConsent || setupInFlight) return;
    if (activeTab !== 'dashboard' && activeTab !== 'calendar') return;
    syncAppointmentsRef.current('ensure');
  }, [user, activeTab, securityGate, pendingConsent, setupInFlight, date, view]);

  // Neue Buchungen und Stornos kommen über die Inbox (Poll alle 15s und bei Tab-Fokus).
  // Schon geladene Zeiträume bleiben beim Tab-Wechsel liegen; nur dieses Signal holt
  // den gerade sichtbaren Bereich neu, ohne einen eigenen Live-Mechanismus im Client.
  const calendarSeenNotificationIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (inbox.fetchedAt === null) return;
    if (!calendarSeenNotificationIdsRef.current) {
      // Erster erfolgreicher Poll dient nur als Ausgangsbasis — sonst würden beim
      // Öffnen des Dashboards alte Buchungen die Eintritts-Animation auslösen.
      calendarSeenNotificationIdsRef.current = new Set(inbox.notifications.map(n => n.id));
      return;
    }
    const seen = calendarSeenNotificationIdsRef.current;
    const fresh = inbox.notifications.filter(n => !seen.has(n.id));
    if (!fresh.length) return;
    fresh.forEach(n => seen.add(n.id));

    const bookedIds = Array.from(new Set(
      fresh.filter(n => n.type === 'booked' && n.appointment_id).map(n => n.appointment_id as string)
    ));
    const cancelledIds = Array.from(new Set(
      fresh.filter(n => n.type === 'cancelled' && n.appointment_id).map(n => n.appointment_id as string)
    ));

    const refreshAndHighlightNewBookings = () => {
      syncAppointmentsRef.current('refresh').then(() => {
        if (!bookedIds.length) return;
        setJustBookedEventIds(prev => new Set([...prev, ...bookedIds]));
        window.setTimeout(() => {
          setJustBookedEventIds(prev => {
            const next = new Set(prev);
            bookedIds.forEach(id => next.delete(id));
            return next;
          });
        }, 1000);
      });
    };

    if (cancelledIds.length) {
      // Kurz ausblenden, bevor der Termin durch den Refresh aus der Liste verschwindet —
      // sonst poppt er ohne jede Animation einfach weg.
      setFadingOutEventIds(prev => new Set([...prev, ...cancelledIds]));
      window.setTimeout(refreshAndHighlightNewBookings, 240);
    } else {
      refreshAndHighlightNewBookings();
    }
    // syncAppointments ist bei jedem Render neu, absichtlich nicht in den Deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inbox.notifications, inbox.fetchedAt]);

  useEffect(() => {
    if (!user || securityGate !== 'ok' || activeTab !== 'chatbot' || pendingConsent || setupInFlight) return;
    let cancelled = false;
    setChatStripeLoadState('loading');
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/admin/stripe/subscription-status`, {
          headers: await getAuthHeader(),
        });
        if (cancelled) return;
        setChatApiActive(
          typeof res.data?.api_active === 'boolean'
            ? res.data.api_active
            : isStripeSubscriptionActiveForApi(res.data?.subscription_status)
        );
        setChatStripeLoadState('ok');
      } catch {
        if (cancelled) return;
        setChatApiActive(false);
        setChatStripeLoadState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // getAuthHeader aus AuthContext ist pro Render neu — nur user/activeTab/securityGate als Trigger
  }, [user, activeTab, securityGate, pendingConsent, setupInFlight]);

  useEffect(() => {
    if (window.innerWidth < 640) setView(Views.DAY);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'cancel') {
      setCheckoutBanner(checkout);
      // URL bereinigen ohne Seitenneuladen
      window.history.replaceState({}, '', window.location.pathname);
      if (checkout === 'success') setTimeout(() => setCheckoutBanner(null), 8000);
    }
  }, []);

  const filteredEvents = useMemo(() => {
    const base = resourceFilter === 'all'
      ? events
      : events.filter((ev: any) => (ev.resourceId || ev.extendedProps?.resourceId) === resourceFilter);
    // Transiente Animations-Flags nur für den einen live aktualisierten Termin anhängen —
    // alle anderen Objekte bleiben unverändert, damit react-big-calendar sie nicht neu rendert.
    if (justBookedEventIds.size === 0 && fadingOutEventIds.size === 0) return base;
    return base.map((ev) => {
      if (justBookedEventIds.has(ev.id)) return { ...ev, _justChanged: true };
      if (fadingOutEventIds.has(ev.id)) return { ...ev, _fading: true };
      return ev;
    });
  }, [events, resourceFilter, justBookedEventIds, fadingOutEventIds]);

  // "Nächste Termine": künftige Termine im zusammenhängend geladenen Fenster ab dieser Woche.
  // Ein später schon geholter Monat zählt nicht mit, solange dazwischen noch eine Lücke ist.
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const coveredUntil = contiguousCoverageEnd(loadedRanges, rangeAroundToday(now, 0).start);
    if (coveredUntil == null) return [];
    const nowMs = now.getTime();
    return [...filteredEvents]
      .filter((ev: any) => {
        const start = ev.start.getTime();
        return start >= nowMs && start <= coveredUntil;
      })
      .sort((a: any, b: any) => a.start.getTime() - b.start.getTime());
  }, [filteredEvents, loadedRanges]);

  const stats = useMemo(() => {
    const today = new Date();
    const todaysEvents = filteredEvents.filter(e => isSameDay(e.start, today));
    const pending = filteredEvents.filter(e => e.status === 'pending').length;
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const thisWeek = filteredEvents.filter(e =>
      isWithinInterval(e.start, { start: weekStart, end: weekEnd })
    ).length;
    return { today: todaysEvents.length, pending, thisWeek };
  }, [filteredEvents]);

  const calendarResources = useMemo(() => {
    const all = fetchedResources.map((r: any) => ({
      id: r.id,
      name: r.name || r.id,
      color: r.color || '#3B82F6',
      capacity: r.capacity,
    }));
    return resourceFilter === 'all' ? all : all.filter((r: any) => r.id === resourceFilter);
  }, [fetchedResources, resourceFilter]);

  const showResourceColumns =
    view !== 'month' && calendarResources.length > 0 && resourceFilter !== 'all';

  const resourceLabelSingular = userSettings?.settings?.resource_label?.singular || 'Ressource';
  const resourceLabelPlural = userSettings?.settings?.resource_label?.plural || 'Ressourcen';

  const appointmentTypes: any[] = useMemo(() => {
    const durations = userSettings?.settings?.appointment_durations;
    return (durations?.mode === 'multiple' && durations?.types?.length) ? durations.types : [];
  }, [userSettings]);

  const calendarComponents: Components = useMemo(() => ({
    toolbar: (props: any) => <CustomToolbar {...props} onView={setView} />,
    event: (props: any) => <CustomEvent {...props} appointmentTypes={appointmentTypes} />,
    resourceHeader: ResourceHeader,
    week: { header: CustomWeekHeader },
    day: { header: CustomWeekHeader },
    month: {
      header: MonthWeekdayHeader,
      dateHeader: CustomMonthDateCell,
      event: (props: any) => <CustomMonthEvent {...props} appointmentTypes={appointmentTypes} />,
    },
  }), [setView, appointmentTypes]);

  const handleSelectEvent = (event: any) => { setSelectedEvent(event); };

  // --- QUICK BOOK LOGIC ---
  const fetchSuggestions = (form: typeof quickBookForm) => {
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    setQuickBookSuggestions([]);
    setQuickBookError('');
    if (!form.date || !form.time) return;
    suggestDebounceRef.current = setTimeout(async () => {
      setQuickBookLoading(true);
      try {
        const res = await axios.post(`${API_BASE_URL}/admin/suggestions`, {
          date: `${form.date}T${form.time}`,
          resource: form.resource || undefined,
          appointmentType: form.appointmentType || undefined,
          bypassGapCheck: !form.gapAware,
          limit: 6,
        }, { headers: await getAuthHeader() });
        const fetched = res.data.suggestions || [];
        setQuickBookSuggestions(fetched);
        if (fetched.length === 0) setQuickBookError('Keine freien Slots gefunden. Anderen Tag / Ressource versuchen oder Lücken-Optimierung deaktivieren.');
      } catch (err: any) {
        const msg = err.response?.data?.error || err.message || 'Fehler beim Laden der Vorschläge.';
        setQuickBookError(`Slot-Suche fehlgeschlagen: ${msg}`);
        setQuickBookSuggestions([]);
      } finally { setQuickBookLoading(false); }
    }, 400);
  };

  const updateQuickBookField = (field: string, value: any) => {
    setQuickBookForm(prev => {
      const updated = { ...prev, [field]: value, selectedStart: '', selectedEnd: '', selectedResource: '' };
      fetchSuggestions(updated);
      return updated;
    });
  };

  const selectSuggestion = (suggestion: any) => {
    const startDate = new Date(suggestion.start);
    const timeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
    const dateStr = format(startDate, 'yyyy-MM-dd');
    const resourceId = suggestion.resource || '';
    setQuickBookForm(prev => ({
      ...prev,
      date: dateStr,
      time: timeStr,
      selectedStart: suggestion.start,
      selectedEnd: suggestion.end,
      selectedResource: resourceId,
    }));
    setQuickBookError('');
  };

  const closeQuickBook = () => {
    setQuickBookOpen(false);
    setQuickBookStep(1);
    setCalendarViewDate(new Date());
    setQuickBookForm({ date: '', time: '', clientName: '', contact: '', resource: '', appointmentType: '', gapAware: true, selectedStart: '', selectedEnd: '', selectedResource: '' });
    setQuickBookSuggestions([]);
    setQuickBookError('');
  };

  const handleSubmitQuickBook = async () => {
    // Read from ref to guarantee we see the latest form state at click time,
    // regardless of any pending React state batches.
    const form = quickBookFormRef.current;
    const resourceId = form.selectedResource || form.resource;
    if (!form.clientName.trim()) { setQuickBookError('Kundenname ist erforderlich.'); return; }
    if (!form.selectedStart) { setQuickBookError('Bitte wähle einen verfügbaren Slot aus der Liste.'); return; }
    if (!resourceId) { setQuickBookError('Bitte wähle eine Ressource aus.'); return; }
    if (appointmentTypes.length > 0 && !form.appointmentType.trim()) {
      setQuickBookError('Bitte wähle in Schritt 1 einen Termintyp aus (auf „Ändern" klicken).');
      return;
    }
    setQuickBookError('');
    setQuickBookSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/admin/book`, {
        dateTimeStart: form.selectedStart,
        resource: resourceId,
        clientName: form.clientName.trim(),
        contact: form.contact.trim() || undefined,
        appointmentType: form.appointmentType || undefined,
        bypassGapCheck: !form.gapAware,
      }, { headers: await getAuthHeader() });
      closeQuickBook();
      void syncAppointments('refresh');
    } catch (err: any) {
      setQuickBookError(err.response?.data?.error || 'Fehler beim Erstellen des Termins.');
    } finally { setQuickBookSubmitting(false); }
  };

  // --- RENDER: AUTH LOADING ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E0B1E] via-[#1A1333] to-[#0E0B1E] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6D00]/30 border-t-[#FF6D00] rounded-full animate-spin" />
      </div>
    );
  }

  // --- RENDER: LOGIN ---
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E0B1E] via-[#1A1333] to-[#0E0B1E] flex items-center justify-center p-4 relative overflow-hidden">
        <Link
          to="/"
          className="fixed top-4 left-4 z-[100] inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-semibold text-white shadow-lg shadow-black/30 bg-[#0E0B1E]/90 hover:bg-[#1A1333] border border-white/20 transition-colors backdrop-blur-md"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          aria-label="Zurück zur Startseite"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white" aria-hidden>
            <ArrowLeft className="h-5 w-5" strokeWidth={2.25} />
          </span>
          Zurück
        </Link>
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF6D00]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00B0FF]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-3/4 left-1/3 w-[300px] h-[300px] bg-[#654F6F]/10 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[380px] relative z-10"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(255,109,0,0.4)] mb-5">
              <span className="text-white font-black text-2xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>C</span>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Candlear Booking</h1>
            <p className="text-[#8B7FA8] text-sm mt-1.5 font-medium">Verwaltung & Terminplanung</p>
          </div>
          <div className="bg-white/6 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-[0_24px_64px_rgba(0,0,0,0.4)]">
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-[#9B8FBB] uppercase tracking-widest mb-2">E-Mail</label>
                <input
                  type="email"
                  className="w-full bg-white/8 border border-white/12 text-white text-sm px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/30 focus:border-[#FF6D00]/50 transition-all placeholder:text-white/30 backdrop-blur-sm"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  placeholder="deine@email.de"
                  required
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-[#9B8FBB] uppercase tracking-widest">Passwort</label>
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-[#9B8FBB] hover:text-[#FF6D00] transition-colors">Vergessen?</button>
                </div>
                <input
                  type="password"
                  className="w-full bg-white/8 border border-white/12 text-white text-sm px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/30 focus:border-[#FF6D00]/50 transition-all placeholder:text-white/30 backdrop-blur-sm"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              {loginError && (
                <p className="text-red-400 text-xs text-center bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{loginError}</p>
              )}
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="submit" disabled={loginLoading}
                className="w-full bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white font-bold text-sm py-3 rounded-xl shadow-[0_8px_24px_rgba(255,109,0,0.4)] transition-all mt-2 disabled:opacity-60"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {loginLoading ? 'Anmelden...' : 'Anmelden'}
              </motion.button>
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-[#9B8FBB]">oder</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="button" onClick={handleGoogleLogin} disabled={loginLoading}
                className="w-full bg-white/8 border border-white/12 text-white font-semibold text-sm py-3 rounded-xl hover:bg-white/12 transition-all flex items-center justify-center gap-3 disabled:opacity-60">
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#EA4335" d="M5.26 9.77A7.23 7.23 0 0 1 12 4.75c1.73 0 3.3.64 4.51 1.68l3.36-3.36A12 12 0 0 0 12 0C7.37 0 3.37 2.71 1.39 6.68l3.87 3.09z"/>
                  <path fill="#34A853" d="M16.04 18.01A7.14 7.14 0 0 1 12 19.25c-2.96 0-5.5-1.78-6.74-4.36l-3.88 2.99A12 12 0 0 0 12 24c3.24 0 6.18-1.17 8.44-3.1l-4.4-2.89z"/>
                  <path fill="#FBBC05" d="M19.25 12c0-.78-.09-1.53-.24-2.25H12v4.26h4.08a3.58 3.58 0 0 1-1.52 2.33l4.4 2.89A11.94 11.94 0 0 0 22.5 12c0-.84-.09-1.65-.25-2.43L19.25 12z" />
                  <path fill="#4285F4" d="M12 4.75a7.23 7.23 0 0 1 4.51 1.68l3.36-3.36A11.97 11.97 0 0 0 12 0c-4.63 0-8.63 2.71-10.61 6.68l3.87 3.09A7.23 7.23 0 0 1 12 4.75z"/>
                  <path fill="#4285F4" d="M5.26 9.77l-3.87-3.09A12.02 12.02 0 0 0 0 12c0 1.94.46 3.77 1.28 5.38l3.98-3.07A7.17 7.17 0 0 1 4.75 12c0-.78.18-1.52.51-2.23z"/>
                </svg>
                Mit Google anmelden
              </motion.button>
              <div className="text-center pt-1">
                <button type="button" onClick={() => setShowRegisterModal(true)} className="text-xs text-[#9B8FBB] hover:text-[#FF6D00] transition-colors font-medium block w-full">
                  Noch kein Konto? Jetzt registrieren →
                </button>
              </div>
              <p className="text-center text-[10px] text-[#654F6F] leading-relaxed pt-1">
                Beim Anlegen eines neuen Kontos bitten wir dich um deine Zustimmung zu{' '}
                <a href="/agb" target="_blank" rel="noreferrer" className="underline hover:text-[#9B8FBB] transition-colors">AGB</a>,{' '}
                <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline hover:text-[#9B8FBB] transition-colors">Datenschutzerklärung</a>
                {' '}und{' '}
                <a href="/avv" target="_blank" rel="noreferrer" className="underline hover:text-[#9B8FBB] transition-colors">AVV</a>.
              </p>
            </form>
          </div>
        </motion.div>
        <AnimatePresence>
          {showForgotPassword && <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />}
        </AnimatePresence>
        <AnimatePresence>
          {showRegisterModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => { setShowRegisterModal(false); setAvvAccepted(false); }} />
              <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="bg-white w-full max-w-md rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.3)] relative z-10 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
                <div className="px-6 py-5 bg-gradient-to-r from-[#FF6D00]/6 to-transparent border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Account erstellen</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Kostenlos registrieren und loslegen</p>
                  </div>
                  <button onClick={() => { setShowRegisterModal(false); setAvvAccepted(false); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                    <Icons.X />
                  </button>
                </div>
                <form onSubmit={handleRegister} className="p-6 space-y-4">
                  {[
                    { label: 'E-Mail', type: 'email', key: 'email', placeholder: 'deine@email.de', required: true },
                    { label: 'Passwort', type: 'password', key: 'password', placeholder: 'Min. 8 Zeichen, Groß-/Kleinbuchstaben + Zahl', required: true, minLength: 8 },
                    { label: 'Passwort bestätigen', type: 'password', key: 'confirmPassword', placeholder: 'Passwort wiederholen', required: true, minLength: 8 },
                    { label: 'Firmenname (optional)', type: 'text', key: 'business_name', placeholder: 'z.B. Fitness Studio Köln', required: false },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                      <input type={field.type} className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/15 focus:border-[#FF6D00] transition-all placeholder:text-gray-400"
                        value={(registerData as any)[field.key]} onChange={e => setRegisterData({ ...registerData, [field.key]: e.target.value })}
                        placeholder={field.placeholder} required={field.required} minLength={field.minLength} />
                    </div>
                  ))}
                  <ConsentCheckbox id="avv-check" checked={avvAccepted} onChange={setAvvAccepted} />
                  <motion.button whileHover={{ scale: avvAccepted ? 1.01 : 1 }} whileTap={{ scale: avvAccepted ? 0.98 : 1 }} type="submit" disabled={!avvAccepted}
                    className="w-full bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white font-bold text-sm py-3 rounded-xl shadow-[0_4px_16px_rgba(255,109,0,0.3)] transition-all mt-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Account erstellen
                  </motion.button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // --- RENDER: ZUSTIMMUNG BEI GOOGLE-ERSTREGISTRIERUNG (CAN-33) ---
  // Muss VOR dem Security-Gate stehen: Der Nutzer ist zu diesem Zeitpunkt bereits bei
  // Firebase angemeldet, hat aber noch kein Firestore-Dokument. Ohne diesen Zweig würde
  // direkt das Dashboard gerendert.
  if (pendingConsent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E0B1E] via-[#1A1333] to-[#0E0B1E] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF6D00]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00B0FF]/8 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[400px] relative z-10"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(255,109,0,0.4)] mb-5">
              <span className="text-white font-black text-2xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>C</span>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Candlear Booking</h1>
          </div>
          <div className="bg-white/6 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-[0_24px_64px_rgba(0,0,0,0.4)] space-y-5">
            <div>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Noch ein Schritt</h2>
              <p className="text-sm text-[#9B8FBB] mt-2 leading-relaxed">
                Für{' '}<span className="text-white font-medium">{user?.email}</span>{' '}besteht noch kein Konto.
                Bevor wir es anlegen, brauchen wir deine Zustimmung.
              </p>
            </div>

            <ConsentCheckbox id="google-consent-check" checked={consentAccepted} onChange={setConsentAccepted} variant="dark" />

            {consentError && (
              <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3.5 py-2.5">{consentError}</p>
            )}

            <div className="space-y-2.5">
              <motion.button
                whileHover={{ scale: consentAccepted && !consentBusy ? 1.01 : 1 }}
                whileTap={{ scale: consentAccepted && !consentBusy ? 0.98 : 1 }}
                type="button"
                onClick={handleConsentConfirm}
                disabled={!consentAccepted || consentBusy}
                className="w-full bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white font-bold text-sm py-3 rounded-xl shadow-[0_4px_16px_rgba(255,109,0,0.3)] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {consentBusy ? 'Konto wird erstellt…' : 'Zustimmen und Konto erstellen'}
              </motion.button>
              <button
                type="button"
                onClick={handleConsentCancel}
                disabled={consentBusy}
                className="w-full text-xs text-[#9B8FBB] hover:text-white transition-colors py-2 disabled:opacity-40"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // --- RENDER: SECURITY GATE (E-Mail-Verifizierung / 2FA) ---
  // setupInFlight zeigt denselben Spinner, damit nach dem Google-Sign-in nicht kurz ein
  // leeres Dashboard aufblitzt, während der Setup-Aufruf noch läuft
  if (securityGate !== 'ok' || setupInFlight) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0E0B1E] via-[#1A1333] to-[#0E0B1E] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF6D00]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00B0FF]/8 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[400px] relative z-10"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(255,109,0,0.4)] mb-5">
              <span className="text-white font-black text-2xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>C</span>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Candlear Booking</h1>
          </div>
          <div className="bg-white/6 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-[0_24px_64px_rgba(0,0,0,0.4)]">
            {(securityGate === 'checking' || setupInFlight) && (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="w-8 h-8 border-2 border-[#FF6D00]/30 border-t-[#FF6D00] rounded-full animate-spin" />
                <p className="text-sm text-[#9B8FBB]">Sicherheitsprüfung läuft…</p>
              </div>
            )}
            {securityGate === 'mfa' && (
              <form onSubmit={handleMfaVerify} className="space-y-5">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Bestätigungscode</h2>
                  <p className="text-sm text-[#9B8FBB] mt-2 leading-relaxed">
                    Gib den 6-stelligen Code ein, den wir an{' '}
                    <span className="text-white font-medium">{user.email}</span>{' '}
                    gesendet haben. Der Code steht in der E-Mail — es gibt keinen Link zum Anklicken.
                  </p>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full bg-white/8 border border-white/12 text-white text-center text-2xl tracking-[0.4em] px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/30 focus:border-[#FF6D00]/50 transition-all placeholder:text-white/20 backdrop-blur-sm"
                  required
                />
                {gateMessage && (
                  <p className="text-xs text-center text-[#FF9A00] bg-[#FF6D00]/10 border border-[#FF6D00]/20 rounded-lg px-3 py-2">{gateMessage}</p>
                )}
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="submit" disabled={gateBusy || mfaCode.length !== 6}
                  className="w-full bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white font-bold text-sm py-3 rounded-xl shadow-[0_8px_24px_rgba(255,109,0,0.4)] transition-all disabled:opacity-60"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {gateBusy ? 'Prüfe…' : 'Code bestätigen'}
                </motion.button>
                <button type="button" onClick={requestMfaCode} disabled={gateBusy}
                  className="w-full text-xs text-[#9B8FBB] hover:text-[#FF6D00] transition-colors font-medium disabled:opacity-60">
                  Code erneut senden
                </button>
              </form>
            )}
            <div className="mt-6 pt-5 border-t border-white/10 text-center">
              <button onClick={handleLogout} className="text-xs text-[#654F6F] hover:text-[#9B8FBB] transition-colors">
                Abmelden und anderes Konto verwenden
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // --- RENDER: DASHBOARD SHELL ---
  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
      <BookingToastHost notifications={inbox.notifications} fetchedAt={inbox.fetchedAt} onRead={inbox.markRead} />

      {/* SIDEBAR OVERLAY (mobile) */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-[#0E0B1E]/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
        )}
      </AnimatePresence>

      {/* SIDEBAR */}
      <aside
        className={cn(
          "flex-shrink-0 flex flex-col overflow-hidden",
          "w-[260px] max-w-[85vw] lg:w-[240px] fixed lg:relative inset-y-0 left-0 h-full z-[45]",
          "transition-transform duration-300",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        style={{ background: '#0E0B1E', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="absolute -top-20 -right-20 w-[200px] h-[200px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,109,0,0.07) 0%, transparent 70%)' }} />

        {/* Logo */}
        <div className="px-5 pt-6 pb-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-[38px] h-[38px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #FF6D00, #FF9A00)', boxShadow: '0 4px 14px rgba(255,109,0,0.4)' }}>
              <span className="text-white font-black text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {(userSettings?.business_name || 'C').charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="text-white font-bold text-sm leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {userSettings?.business_name || 'Candlear Booking'}
              </div>
              <div className="text-[10px] font-semibold tracking-[0.08em] uppercase" style={{ color: '#654F6F' }}>Booking</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 pt-3 pb-2 overflow-y-auto">
          <div className="text-[9px] font-bold uppercase tracking-[0.1em] px-2 py-2" style={{ color: 'rgba(255,255,255,0.2)' }}>Navigation</div>
          <NavButton active={activeTab === 'dashboard'} onClick={() => handleTabChange('dashboard')} icon={<Icons.Grid />} label="Dashboard" />
          <NavButton active={activeTab === 'calendar'} onClick={() => handleTabChange('calendar')} icon={<Icons.Calendar />} label="Kalender" />
          <NavButton active={activeTab === 'clients'} onClick={() => handleTabChange('clients')} icon={<Icons.Users />} label="Kunden" />
          <NavButton active={activeTab === 'chatbot'} onClick={() => handleTabChange('chatbot')} icon={<Icons.MessageSquare />} label="Chat-Bot" />
          <NavButton active={activeTab === 'templates'} onClick={() => handleTabChange('templates')} icon={<Icons.Layout />} label="Templates" />
        </nav>

        {/* User footer */}
        <div ref={accountMenuRef} className="relative px-3 pb-5 pt-3 border-t border-white/5">
          <AnimatePresence>
            {accountMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-3 right-3 bottom-full mb-2 rounded-xl overflow-hidden z-10 origin-bottom"
                style={{
                  background: '#1A1628',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
                }}
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    handleTabChange('settings');
                    setAccountMenuOpen(false);
                  }}
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 text-left transition-colors"
                  style={{ color: activeTab === 'settings' ? '#FF6D00' : 'rgba(255,255,255,0.85)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                >
                  <Icons.Settings />
                  <span className="text-[12px] font-semibold">Einstellungen</span>
                </button>
                <div className="mx-2" style={{ height: '1px', background: 'rgba(255,255,255,0.06)' }} />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccountMenuOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 text-left transition-colors"
                  style={{ color: '#6B5F80' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.color = 'white';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.color = '#6B5F80';
                    (e.currentTarget as HTMLElement).style.background = '';
                  }}
                >
                  <Icons.LogOut />
                  <span className="text-[12px] font-semibold">Abmelden</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <button
            type="button"
            onClick={() => setAccountMenuOpen(open => !open)}
            className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-xl text-left transition-colors"
            style={{
              background: accountMenuOpen || activeTab === 'settings' ? 'rgba(255,109,0,0.10)' : undefined,
            }}
            onMouseEnter={e => {
              if (!accountMenuOpen && activeTab !== 'settings') {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.05)';
              }
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background =
                accountMenuOpen || activeTab === 'settings' ? 'rgba(255,109,0,0.10)' : '';
            }}
            aria-label="Mein Account"
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
          >
            <div className="w-8 h-8 rounded-[10px] flex items-center justify-center text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, #654F6F, #8B69A0)' }}>
              <Icons.User />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold leading-tight truncate" style={{ color: accountMenuOpen || activeTab === 'settings' ? '#FF6D00' : 'rgba(255,255,255,0.85)' }}>Mein Account</div>
              <div className="text-[10px] leading-tight truncate" style={{ color: '#654F6F' }}>{user?.email || ''}</div>
            </div>
            <span
              className="flex-shrink-0 transition-transform duration-200"
              style={{
                color: accountMenuOpen || activeTab === 'settings' ? '#FF6D00' : '#6B5F80',
                transform: accountMenuOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}
            >
              <Icons.ChevronRight />
            </span>
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden" style={{ background: '#F0F2FA' }}>

        {/* HEADER */}
        <header className="h-14 flex items-center justify-between bg-white border-b border-[#E8EAF6] flex-shrink-0 px-4 lg:px-8 sticky top-0 z-20" style={{ boxShadow: '0 1px 8px rgba(14,11,30,0.04)' }}>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => setSidebarOpen(true)} aria-label="Navigation öffnen" className="lg:hidden -ml-1.5 w-10 h-10 flex-shrink-0 flex items-center justify-center text-[#654F6F] hover:bg-[#F4F5FC] active:bg-[#E8EAF6] rounded-lg transition-colors">
              <Icons.Menu />
            </button>
            <div className="text-[13px] font-bold text-[#0E0B1E] truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {activeTab === 'dashboard' ? 'Übersicht' : activeTab === 'calendar' ? 'Kalender' : activeTab === 'clients' ? 'Kunden' : activeTab === 'chatbot' ? 'Chat-Bot' : activeTab === 'templates' ? 'Templates' : 'Einstellungen'}
            </div>
            <div className="hidden md:block w-[5px] h-[5px] rounded-full bg-[#FF6D00]" />
            <div className="hidden md:block text-[11px] font-medium" style={{ color: '#9CA3AF' }}>
              {format(new Date(), 'EEEE, d. MMMM yyyy', { locale: de })}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0">
            {activeTab === 'calendar' && (
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={() => {
                setQuickBookForm({ date: '', time: roundToHalfHour(), clientName: '', contact: '', resource: '', appointmentType: '', gapAware: true, selectedStart: '', selectedEnd: '', selectedResource: '' });
                setQuickBookStep(1);
                setQuickBookSuggestions([]);
                setQuickBookError('');
                setCalendarViewDate(new Date());
                setQuickBookOpen(true);
              }}
                aria-label="Neuer Termin"
                className="flex items-center gap-1.5 text-white text-[12px] font-bold h-10 sm:h-auto px-3 sm:px-3.5 sm:py-2 rounded-[10px]"
                style={{ background: 'linear-gradient(135deg, #FF6D00, #FF9A00)', boxShadow: '0 4px 12px rgba(255,109,0,0.32)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <Icons.Plus />
                <span className="hidden sm:inline">Neuer Termin</span>
              </motion.button>
            )}
            <InboxPopover inbox={inbox} />
          </div>
        </header>

        {/* CHECKOUT BANNER */}
        <AnimatePresence>
          {checkoutBanner === 'success' && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-3 px-6 py-3 bg-emerald-50 border-b border-emerald-200">
              <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                <Icons.Check />
              </div>
              <p className="text-sm font-semibold text-emerald-800">
                Willkommen! Dein 30-Tage-Trial hat gestartet. Deinen API-Key findest du im Tab <span className="underline cursor-pointer" onClick={() => setActiveTab('chatbot')}>Chat-Bot</span>.
              </p>
              <button onClick={() => setCheckoutBanner(null)} className="ml-auto text-emerald-600 hover:text-emerald-800 transition-colors">
                <Icons.X />
              </button>
            </motion.div>
          )}
          {checkoutBanner === 'cancel' && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-3 px-6 py-3 bg-amber-50 border-b border-amber-200">
              <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                <Icons.XCircle />
              </div>
              <p className="text-sm font-semibold text-amber-800">
                Zahlung abgebrochen. Du kannst dein Abo jederzeit unter <span className="underline cursor-pointer" onClick={() => { setActiveTab('settings'); }}>Einstellungen → Abonnement</span> einrichten.
              </p>
              <button onClick={() => setCheckoutBanner(null)} className="ml-auto text-amber-600 hover:text-amber-800 transition-colors">
                <Icons.X />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CONTENT */}
        <div className="flex-1 overflow-auto px-4 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pt-6 lg:px-8 lg:pt-7 lg:pb-7">
          <AnimatePresence mode="wait">

            {/* DASHBOARD TAB */}
            {activeTab === 'dashboard' && (
              <motion.div key="dashboard" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <StatCard title="Termine Heute" value={stats.today} colorClass="orange" icon={<Icons.Calendar />} />
                  <StatCard title="Ausstehend" value={stats.pending} colorClass="purple" icon={<Icons.Clock />} />
                  <StatCard title="Termine diese Woche" value={stats.thisWeek} colorClass="sky" icon={<Icons.Check />} />
                </div>

                {/* Today grid */}
                {(() => {
                  const now = new Date();
                  const todayEvts = filteredEvents.filter(ev => isSameDay(ev.start, now)).sort((a, b) => a.start.getTime() - b.start.getTime());
                  const todayAllEvts = filteredEvents.filter(ev => isSameDay(ev.start, now));
                  const todayUnfiltered = events.filter(ev => isSameDay(ev.start, now));
                  const resourcesToShow = fetchedResources;
                  const countPerResource = resourcesToShow.map(r => ({
                    ...r,
                    count: todayAllEvts.filter(ev => (ev.resourceId || ev.extendedProps?.resourceId) === r.id).length,
                  }));
                  const maxCount = Math.max(...countPerResource.map(r => r.count), 1);
                  const totalToday = todayAllEvts.length;
                  const selectedResource = resourceFilter === 'all'
                    ? null
                    : fetchedResources.find((r: any) => r.id === resourceFilter);
                  const filterLabel = selectedResource
                    ? `${selectedResource.name || selectedResource.id}${typeof selectedResource.capacity === 'number' ? ` · ${selectedResource.capacity} Pers.` : ''}`
                    : `Alle ${resourceLabelPlural}`;
                  const auslastungExpanded = auslastungOpen ?? fetchedResources.length <= 5;

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 mb-6">
                      {/* Today Card */}
                      <div className="bg-white rounded-[18px] border border-[#E8EAF6] flex flex-col min-h-0 overflow-hidden self-stretch" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                        <div className="px-[22px] py-[18px] pb-[14px] border-b border-[#E8EAF6] flex items-center justify-between flex-shrink-0 gap-3">
                          <div className="min-w-0">
                            <div className="text-[13px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              Heute — {format(now, 'd. MMMM', { locale: de })}
                            </div>
                            <div className="text-[11px] mt-[1px]" style={{ color: '#654F6F' }}>
                              Tagesübersicht · {totalToday} {totalToday === 1 ? 'Termin' : 'Termine'}
                            </div>
                          </div>
                          {fetchedResources.length > 0 && (
                            <div ref={todayFilterRef} className="relative flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => setTodayFilterOpen(open => !open)}
                                className="flex items-center gap-2 max-w-[190px] px-2.5 py-1.5 rounded-[10px] text-left transition-colors"
                                style={{ background: todayFilterOpen ? 'rgba(255,109,0,0.08)' : '#F4F5FC' }}
                                aria-haspopup="listbox"
                                aria-expanded={todayFilterOpen}
                                aria-label="Ressource filtern"
                              >
                                <span className="text-[11px] font-semibold truncate" style={{ color: todayFilterOpen ? '#FF6D00' : '#0E0B1E' }}>
                                  {filterLabel}
                                </span>
                                <span
                                  className="flex-shrink-0 transition-transform duration-200"
                                  style={{
                                    color: todayFilterOpen ? '#FF6D00' : '#654F6F',
                                    transform: todayFilterOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                                  }}
                                >
                                  <Icons.ChevronRight />
                                </span>
                              </button>
                              <AnimatePresence>
                                {todayFilterOpen && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 6, scale: 0.97 }}
                                    transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
                                    className="absolute right-0 top-full mt-1.5 min-w-[220px] rounded-[12px] overflow-hidden z-20 origin-top-right"
                                    style={{
                                      background: '#fff',
                                      border: '1px solid #E8EAF6',
                                      boxShadow: '0 12px 32px rgba(14,11,30,0.12)',
                                    }}
                                    role="listbox"
                                  >
                                    <button
                                      type="button"
                                      role="option"
                                      aria-selected={resourceFilter === 'all'}
                                      onClick={() => {
                                        setResourceFilter('all');
                                        setTodayFilterOpen(false);
                                      }}
                                      className={cn(
                                        "flex items-center gap-2 w-full px-3 py-2 text-left transition-colors",
                                        resourceFilter === 'all' ? "bg-[#FF6D00]/8" : "hover:bg-[#F4F5FC]"
                                      )}
                                    >
                                      <span className={cn("text-[12px] font-semibold flex-1", resourceFilter === 'all' ? "text-[#FF6D00]" : "text-[#0E0B1E]")}>
                                        Alle {resourceLabelPlural}
                                      </span>
                                      <span className="text-[11px] font-bold" style={{ color: '#654F6F' }}>{todayUnfiltered.length}</span>
                                    </button>
                                    {fetchedResources.map((r: any) => {
                                      const count = todayUnfiltered.filter(e => (e.resourceId || e.extendedProps?.resourceId) === r.id).length;
                                      const selected = resourceFilter === r.id;
                                      return (
                                        <button
                                          key={r.id}
                                          type="button"
                                          role="option"
                                          aria-selected={selected}
                                          onClick={() => {
                                            setResourceFilter(r.id);
                                            setTodayFilterOpen(false);
                                          }}
                                          className={cn(
                                            "flex items-center gap-2 w-full px-3 py-2 text-left transition-colors",
                                            selected ? "bg-[#FF6D00]/8" : "hover:bg-[#F4F5FC]"
                                          )}
                                        >
                                          <span className={cn("text-[12px] font-semibold flex-1 truncate", selected ? "text-[#FF6D00]" : "text-[#0E0B1E]")}>
                                            {r.name || r.id}{typeof r.capacity === 'number' ? ` · ${r.capacity} Pers.` : ''}
                                          </span>
                                          <span className="text-[11px] font-bold" style={{ color: '#654F6F' }}>{count}</span>
                                        </button>
                                      );
                                    })}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </div>
                        <div className={cn("overflow-y-auto px-1 py-1", auslastungExpanded ? "flex-1 min-h-0" : "max-h-[280px]")}>
                          {todayEvts.length > 0 ? todayEvts.map(ev => (
                            <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                              className="flex gap-[14px] items-start px-3 py-[10px] rounded-xl cursor-pointer hover:bg-[#F4F5FC] transition-colors">
                              <div className="text-[11px] font-bold text-[#0E0B1E] tabular-nums w-[42px] flex-shrink-0 pt-[2px]">{format(ev.start, 'HH:mm')}</div>
                              <div className="rounded-[2px] flex-shrink-0 self-stretch" style={{ width: '3px', minHeight: '40px', background: ev.extendedProps?.resourceColor || '#FF6D00' }} />
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold text-[#0E0B1E] truncate">{ev.title.split('(')[0]}</div>
                                <div className="text-[11px] mt-[2px]" style={{ color: '#654F6F' }}>
                                  {Math.round((ev.end.getTime() - ev.start.getTime()) / 60000)} Min.{ev.extendedProps?.resourceName ? ` · ${ev.extendedProps.resourceName}` : ''}
                                </div>
                              </div>
                            </div>
                          )) : (
                            <div className="flex flex-col items-center justify-center py-10 text-center">
                              <div className="w-10 h-10 bg-[#F4F5FC] rounded-xl flex items-center justify-center mb-3" style={{ color: '#654F6F' }}><Icons.Calendar /></div>
                              <p className="text-[13px] font-semibold" style={{ color: '#654F6F' }}>Keine Termine heute</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right column */}
                      <div className="flex flex-col gap-[14px]">
                        {/* Auslastung */}
                        <div className="bg-white rounded-[18px] border border-[#E8EAF6] overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                          <button
                            type="button"
                            onClick={() => setAuslastungOpen(!auslastungExpanded)}
                            aria-expanded={auslastungExpanded}
                            className={cn(
                              "w-full px-[22px] py-[16px] flex items-center justify-between text-left transition-colors hover:bg-[#F4F5FC]",
                              auslastungExpanded && "border-b border-[#E8EAF6]"
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div>
                                <div className="text-[13px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Auslastung</div>
                                <div className="text-[11px] mt-[1px]" style={{ color: '#654F6F' }}>Heute</div>
                              </div>
                              <span
                                className="flex-shrink-0 transition-transform duration-200"
                                style={{
                                  color: auslastungExpanded ? '#FF6D00' : '#654F6F',
                                  transform: auslastungExpanded ? 'rotate(-90deg)' : 'rotate(0deg)',
                                }}
                              >
                                <Icons.ChevronRight />
                              </span>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-[28px] font-bold text-[#0E0B1E] leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{totalToday}</div>
                              <div className="text-[10px]" style={{ color: '#654F6F' }}>Termine</div>
                            </div>
                          </button>
                          <AnimatePresence initial={false}>
                            {auslastungExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                                className="overflow-hidden"
                              >
                                <div className="px-[18px] py-[14px] space-y-[10px]">
                                  {resourcesToShow.length > 0 ? countPerResource.map(r => {
                                    const pct = maxCount > 0 ? Math.round((r.count / maxCount) * 100) : 0;
                                    return (
                                      <div key={r.id}>
                                        <div className="flex items-center justify-between mb-[5px]">
                                          <div className="flex items-center gap-[7px]">
                                            <div className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: r.color || '#654F6F' }} />
                                            <span className="text-[12px] font-semibold text-[#0E0B1E]">{r.name || r.id}{typeof r.capacity === 'number' ? ` · ${r.capacity} Pers.` : ''}</span>
                                          </div>
                                          <span className="text-[11px] font-bold" style={{ color: '#654F6F' }}>{r.count}</span>
                                        </div>
                                        <div className="rounded-[3px] overflow-hidden" style={{ height: '5px', background: '#F4F5FC' }}>
                                          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                            className="h-full rounded-[3px]" style={{ backgroundColor: r.color || '#FF6D00' }} />
                                        </div>
                                      </div>
                                    );
                                  }) : (
                                    <div className="rounded-[3px] overflow-hidden" style={{ height: '5px', background: '#F4F5FC' }}>
                                      <motion.div initial={{ width: 0 }} animate={{ width: totalToday > 0 ? '75%' : '0%' }} transition={{ duration: 0.6 }}
                                        className="h-full rounded-[3px]" style={{ background: 'linear-gradient(90deg, #FF6D00, #FF9A00)' }} />
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Right column */}
                    </div>
                  );
                })()}

                {/* Nächste Termine */}
                <div>
                  <div className="flex items-center justify-between mb-[14px]">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: '#654F6F' }}>Nächste Termine</div>
                    <button onClick={() => { void syncAppointments('refresh'); }} className="w-7 h-7 flex items-center justify-center rounded-lg transition-all" style={{ color: '#654F6F' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#FF6D00'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,109,0,0.08)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#654F6F'; (e.currentTarget as HTMLElement).style.background = ''; }}>
                      <Icons.Refresh />
                    </button>
                  </div>
                  <div className="bg-white rounded-[18px] border border-[#E8EAF6] overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                    {upcomingEvents.slice(0, 6).map((ev) => (
                      <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                        className="flex items-center gap-3 px-5 py-3 border-b border-[#F8F9FE] last:border-none hover:bg-[#F4F5FC] transition-colors cursor-pointer">
                        <div style={{ width: '52px' }} className="flex-shrink-0">
                          <div className="text-[13px] font-bold text-[#0E0B1E]">{format(ev.start, 'EEE', { locale: de })}</div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: '#654F6F' }}>{format(ev.start, 'd. MMM', { locale: de })}</div>
                        </div>
                        <div className="rounded-[2px] flex-shrink-0" style={{ width: '3px', height: '36px', background: ev.extendedProps?.resourceColor || '#FF6D00' }} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold" style={{ color: '#374151' }}>{ev.title.split('(')[0]}</div>
                          <div className="text-[11px] mt-[1px]" style={{ color: '#654F6F' }}>
                            {ev.extendedProps?.resourceName ? `${ev.extendedProps.resourceName} · ` : ''}{format(ev.start, 'HH:mm')} – {format(ev.end, 'HH:mm')}
                          </div>
                        </div>
                      </div>
                    ))}
                    {upcomingEvents.length === 0 && (
                      <div className="p-12 text-center">
                        <div className="w-12 h-12 bg-[#F4F5FC] rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ color: '#654F6F' }}><Icons.Calendar /></div>
                        <p className="text-[13px] font-medium" style={{ color: '#654F6F' }}>Keine anstehenden Termine</p>
                      </div>
                    )}
                  </div>
                </div>

              </motion.div>
            )}

            {/* CALENDAR TAB */}
            {activeTab === 'calendar' && (
              <motion.div key="calendar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                className="flex flex-col h-[calc(100dvh-112px)] min-h-[460px] lg:h-[calc(100dvh-160px)]">
                {/* Filter pills */}
                <div className="flex items-center mb-3 sm:mb-[14px] flex-shrink-0 min-w-0">
                  <div className="flex max-w-full bg-white border border-[#E8EAF6] p-1 rounded-xl gap-0.5 overflow-x-auto" style={{ boxShadow: '0 1px 6px rgba(14,11,30,0.04)' }}>
                    <button onClick={() => setResourceFilter('all')}
                      className={cn("flex items-center gap-[6px] px-3 py-[5px] rounded-[8px] text-[11px] font-bold uppercase tracking-[0.04em] transition-all whitespace-nowrap",
                        resourceFilter === 'all' ? "bg-[#0E0B1E] text-white" : "text-[#654F6F] hover:bg-[#F4F5FC] hover:text-[#0E0B1E]")}>
                      Alle
                    </button>
                    {fetchedResources.map((r: any) => (
                      <button key={r.id} onClick={() => setResourceFilter(r.id)}
                        className={cn("flex items-center gap-[6px] px-3 py-[5px] rounded-[8px] text-[11px] font-bold uppercase tracking-[0.04em] transition-all whitespace-nowrap",
                          resourceFilter === r.id ? "bg-[#0E0B1E] text-white" : "text-[#654F6F] hover:bg-[#F4F5FC] hover:text-[#0E0B1E]")}>
                        <span className="w-[7px] h-[7px] rounded-full inline-block" style={{ background: r.color || '#3B82F6' }} />
                        {r.name || r.id}{typeof r.capacity === 'number' ? ` · ${r.capacity} Pers.` : ''}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Calendar card */}
                <div className="flex-1 bg-white rounded-[18px] border border-[#E8EAF6] flex flex-col min-h-0 overflow-x-auto overflow-y-hidden" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', minWidth: showResourceColumns && calendarResources.length > 1 ? `${calendarResources.length * 160 + 80}px` : '100%' }}>
                    <Calendar
                      localizer={localizer}
                      events={filteredEvents}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%' }}
                      defaultView={Views.WEEK}
                      view={view}
                      onView={setView}
                      date={date}
                      onNavigate={setDate}
                      views={['month', 'week', 'day']}
                      culture="de"
                      scrollToTime={new Date(0, 0, 0, 8, 0, 0)}
                      components={calendarComponents}
                      onSelectEvent={handleSelectEvent}
                      messages={{ next: "Weiter", previous: "Zurück", today: "Heute", month: "Monat", week: "Woche", day: "Tag" }}
                      dayLayoutAlgorithm="no-overlap"
                      resources={showResourceColumns ? calendarResources : undefined}
                      resourceIdAccessor={(r: any) => r.id}
                      resourceTitleAccessor={(r: any) => r.name}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* CLIENTS TAB */}
            {activeTab === 'clients' && (
              <motion.div key="clients" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <div className="bg-white rounded-[18px] border border-[#E8EAF6] overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                  <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E8EAF6] flex-wrap">
                    <div>
                      <div className="text-[13px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Kunden</div>
                      <div className="text-[11px] font-medium" style={{ color: '#9CA3AF' }}>
                        {clients.length} Kunde{clients.length === 1 ? '' : 'n'} · aus Buchungen ermittelt
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-[7px] rounded-xl w-full sm:w-[240px]" style={{ background: '#F4F5FC' }}>
                      <Icons.Search />
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        placeholder="Name oder Kontakt suchen..."
                        className="bg-transparent border-none focus:outline-none text-xs font-medium w-full"
                        style={{ color: '#374151' }}
                      />
                    </div>
                  </div>

                  {clientsLoading ? (
                    <div className="px-5 py-10 text-center text-[12px] font-medium" style={{ color: '#9CA3AF' }}>Lade Kunden…</div>
                  ) : (
                    (() => {
                      const q = clientSearch.trim().toLowerCase();
                      const filtered = q
                        ? clients.filter((c: any) =>
                            (c.name || '').toLowerCase().includes(q) || (c.contact || '').toLowerCase().includes(q))
                        : clients;

                      if (filtered.length === 0) {
                        return (
                          <div className="px-5 py-10 text-center text-[12px] font-medium" style={{ color: '#9CA3AF' }}>
                            {clients.length === 0 ? 'Noch keine Kunden vorhanden.' : 'Keine Kunden gefunden.'}
                          </div>
                        );
                      }

                      const deleteButton = (client: (typeof filtered)[number]) => (
                        <button
                          onClick={() => handleDeleteClient(client)}
                          disabled={deletingClientId === client.id}
                          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold px-3 min-h-9 sm:min-h-0 sm:py-[7px] rounded-[8px] transition-colors disabled:opacity-50 flex-shrink-0"
                          style={{ color: '#DC2626', background: 'rgba(220,38,38,0.08)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(220,38,38,0.15)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(220,38,38,0.08)'; }}
                        >
                          <Icons.Trash />
                          {deletingClientId === client.id ? 'Löscht…' : 'Löschen'}
                        </button>
                      );

                      return (
                        <>
                        {/* Handy: Liste statt Tabelle */}
                        <ul className="sm:hidden">
                          {filtered.map((client) => (
                            <li key={client.id} className="flex items-center gap-3 px-4 py-3 border-t border-[#F0F2FA] first:border-t-0">
                              <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-semibold text-[#0E0B1E] truncate">{client.name}</div>
                                <div className="text-[12px] font-medium truncate" style={{ color: '#654F6F' }}>{client.contact || '—'}</div>
                                <div className="text-[11px] font-medium mt-0.5" style={{ color: '#9CA3AF' }}>
                                  {client.appointment_count} {client.appointment_count === 1 ? 'Termin' : 'Termine'}
                                </div>
                              </div>
                              {deleteButton(client)}
                            </li>
                          ))}
                        </ul>
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="text-[10px] font-bold uppercase tracking-[0.06em]" style={{ color: '#9CA3AF' }}>
                                <th className="px-5 py-3">Name</th>
                                <th className="px-5 py-3">Kontakt</th>
                                <th className="px-5 py-3">Termine</th>
                                <th className="px-5 py-3 text-right">Aktion</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map((client: any) => (
                                <tr key={client.id} className="border-t border-[#F0F2FA]">
                                  <td className="px-5 py-3 text-[12px] font-semibold text-[#0E0B1E]">{client.name}</td>
                                  <td className="px-5 py-3 text-[12px] font-medium" style={{ color: '#654F6F' }}>{client.contact || '—'}</td>
                                  <td className="px-5 py-3 text-[12px] font-medium" style={{ color: '#654F6F' }}>{client.appointment_count}</td>
                                  <td className="px-5 py-3 text-right">{deleteButton(client)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        </>
                      );
                    })()
                  )}
                </div>
              </motion.div>
            )}

            {/* TEMPLATES TAB */}
            {activeTab === 'templates' && (
              <motion.div key="templates" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <AdminTemplates
                  getToken={() => user!.getIdToken()}
                  onApplied={() => { fetchUserSettings(); void syncAppointments('refresh'); }}
                />
              </motion.div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === 'settings' && (
              <motion.div key="settings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <AdminSettings getToken={() => user!.getIdToken()} onResourceChange={() => { void syncAppointments('refresh'); }} />
              </motion.div>
            )}

            {/* CHATBOT TAB */}
            {activeTab === 'chatbot' && (
              <motion.div key="chatbot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-5">

                  {/* Left: Config + Snippet */}
                  <div className="flex flex-col gap-4">
                    {chatStripeLoadState === 'loading' && (
                      <div className="rounded-[14px] border border-[#E8EAF6] px-4 py-3 text-[12px] font-medium" style={{ background: '#F8F9FE', color: '#654F6F' }}>
                        Abo-Status wird geladen…
                      </div>
                    )}
                    {chatStripeLoadState === 'error' && (
                      <div className="rounded-[14px] border border-red-200 px-4 py-3 flex items-start gap-3" style={{ background: '#FEF2F2' }}>
                        <div className="w-8 h-8 rounded-[10px] bg-red-500 flex items-center justify-center text-white flex-shrink-0">
                          <Icons.XCircle />
                        </div>
                        <p className="text-[13px] font-semibold text-red-900 leading-snug">
                          Abo-Status konnte nicht geladen werden. Bitte Seite neu laden oder später erneut auf den Tab „Chat-Bot“ wechseln.
                        </p>
                      </div>
                    )}
                    {chatStripeLoadState === 'ok' && !chatApiActive && (
                      <div className="rounded-[14px] border border-red-200 px-4 py-3 flex items-start gap-3" style={{ background: '#FEF2F2' }}>
                        <div className="w-8 h-8 rounded-[10px] bg-red-500 flex items-center justify-center text-white flex-shrink-0">
                          <Icons.XCircle />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-red-900 leading-snug">
                            Ohne aktives Abonnement sind der Chat-Bot und das eingebettete Widget <span className="whitespace-nowrap">deaktiviert</span> (die API lehnt Anfragen ab). Bitte unter{' '}
                            <button
                              type="button"
                              onClick={() => setActiveTab('settings')}
                              className="underline font-bold text-red-950 hover:text-red-800"
                            >
                              Einstellungen → Abonnement
                            </button>{' '}
                            ein aktives Abo abschließen oder reaktivieren.
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Bot Config Card */}
                    <div className="bg-white rounded-[18px] border border-[#E8EAF6] overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                      <div className="px-[22px] py-[18px] pb-[14px] border-b border-[#E8EAF6] flex items-center justify-between">
                        <div>
                          <div className="text-[13px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Bot-Konfiguration</div>
                          <div className="text-[11px] mt-[1px]" style={{ color: '#654F6F' }}>Passe deinen Chatbot an</div>
                        </div>
                        <div className="w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: 'rgba(255,109,0,0.08)', color: '#FF6D00' }}>
                          <Icons.MessageSquare />
                        </div>
                      </div>
                      <div className="p-[22px]">
                        {botConfig ? (
                          <form onSubmit={(e) => {
                            e.preventDefault();
                            const fd = new FormData(e.target as HTMLFormElement);
                            const enabledCheckbox = (e.target as HTMLFormElement).querySelector('#bot-enabled') as HTMLInputElement;
                            saveBotConfig({
                              enabled: enabledCheckbox?.checked ?? botConfig.enabled,
                              greeting_message: fd.get('greeting_message') as string,
                              system_prompt: fd.get('system_prompt') as string,
                              personality: botConfig.personality,
                              widget_settings: {
                                position: fd.get('position') as string,
                                title: fd.get('title') as string,
                                privacy_url: fd.get('privacy_url') as string,
                              }
                            });
                          }} className="space-y-4">
                            <div className="flex items-center gap-[10px] px-[14px] py-[10px] rounded-[10px]" style={{ background: '#F4F5FC' }}>
                              <input type="checkbox" id="bot-enabled" name="enabled" defaultChecked={botConfig.enabled} className="w-4 h-4 rounded accent-[#FF6D00] cursor-pointer" />
                              <label htmlFor="bot-enabled" className="text-[13px] font-semibold text-[#0E0B1E] cursor-pointer">Chat-Bot aktivieren</label>
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-[0.08em] mb-[7px]" style={{ color: '#654F6F' }}>Begrüßungstext</label>
                              <input type="text" name="greeting_message" defaultValue={botConfig.greeting_message} placeholder="Hallo! Wie kann ich dir helfen?"
                                className="w-full text-[13px] px-3 py-[9px] rounded-[10px] outline-none focus:ring-2 focus:ring-[#FF6D00]/15 focus:border-[#FF6D00] transition-all"
                                style={{ background: '#F8F9FE', border: '1px solid #E8EAF6', color: '#0E0B1E' }} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-[0.08em] mb-[7px]" style={{ color: '#654F6F' }}>System-Prompt</label>
                              <textarea name="system_prompt" defaultValue={botConfig.system_prompt} rows={3} placeholder="Zusätzliche Anweisungen..."
                                className="w-full text-[13px] px-3 py-[9px] rounded-[10px] outline-none resize-none"
                                style={{ background: '#F8F9FE', border: '1px solid #E8EAF6', color: '#0E0B1E' }} />
                            </div>
                            <div className="border-t border-[#E8EAF6] pt-4">
                              <div className="text-[10px] font-bold uppercase tracking-[0.1em] mb-[14px]" style={{ color: '#654F6F' }}>Widget-Einstellungen</div>
                              <div className="mb-[14px]">
                                <label className="block text-[10px] font-bold uppercase tracking-[0.08em] mb-[7px]" style={{ color: '#654F6F' }}>Position</label>
                                <select name="position" defaultValue={botConfig.widget_settings?.position || 'bottom-right'}
                                  className="w-full text-[13px] px-3 py-[9px] rounded-[10px] outline-none cursor-pointer"
                                  style={{ background: '#F8F9FE', border: '1px solid #E8EAF6', color: '#0E0B1E' }}>
                                  <option value="bottom-right">Unten rechts</option><option value="bottom-left">Unten links</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold uppercase tracking-[0.08em] mb-[7px]" style={{ color: '#654F6F' }}>Widget-Titel</label>
                                <input type="text" name="title" defaultValue={botConfig.widget_settings?.title || 'Chat mit uns'}
                                  className="w-full text-[13px] px-3 py-[9px] rounded-[10px] outline-none"
                                  style={{ background: '#F8F9FE', border: '1px solid #E8EAF6', color: '#0E0B1E' }} />
                              </div>
                              <div className="mt-[14px]">
                                <label className="block text-[10px] font-bold uppercase tracking-[0.08em] mb-[7px]" style={{ color: '#654F6F' }}>Datenschutz-URL</label>
                                <input type="url" name="privacy_url" defaultValue={botConfig.widget_settings?.privacy_url || ''} placeholder="https://deine-website.de/datenschutz"
                                  className="w-full text-[13px] px-3 py-[9px] rounded-[10px] outline-none"
                                  style={{ background: '#F8F9FE', border: '1px solid #E8EAF6', color: '#0E0B1E' }} />
                              </div>

                              {/* Datenschutz-Baustein: copy-paste Textvorschlag für die Erklärung des Mandanten */}
                              <div className="mt-[14px] rounded-[12px] border border-[#E8EAF6] overflow-hidden" style={{ background: '#F8F9FE' }}>
                                <button
                                  type="button"
                                  onClick={() => setPrivacyBausteinOpen(o => !o)}
                                  className="w-full flex items-center justify-between gap-3 px-[14px] py-[11px] text-left"
                                >
                                  <div className="flex items-center gap-[9px] min-w-0">
                                    <div className="w-7 h-7 rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,109,0,0.1)', color: '#CC5500' }}>
                                      <Icons.FileText />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="text-[12px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Baustein für deine Datenschutzerklärung</div>
                                      <div className="text-[10.5px] mt-[1px]" style={{ color: '#654F6F' }}>Textvorschlag zu Chatbot &amp; Google Gemini – zum Ergänzen deiner eigenen Erklärung</div>
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 transition-transform" style={{ color: '#654F6F', transform: privacyBausteinOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                    <Icons.ChevronDown />
                                  </div>
                                </button>
                                <AnimatePresence initial={false}>
                                  {privacyBausteinOpen && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.2 }}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <div className="px-[14px] pb-[14px]">
                                        <p className="text-[11px] leading-relaxed mb-[10px]" style={{ color: '#654F6F' }}>
                                          Unverbindlicher Textvorschlag, keine Rechtsberatung. Bitte die Platzhalter in eckigen Klammern direkt im Feld ausfüllen und den Absatz in deine bestehende Datenschutzerklärung einfügen (z.&nbsp;B. unter „Eingesetzte Dienstleister"). Falls du besondere Datenkategorien nach Art.&nbsp;9 DSGVO verarbeitest (z.&nbsp;B. Gesundheitsdaten), lass den Text zusätzlich anwaltlich prüfen.
                                        </p>
                                        <textarea
                                          value={privacyBausteinText ?? buildPrivacyBaustein()}
                                          onChange={(e) => setPrivacyBausteinText(e.target.value)}
                                          rows={14}
                                          spellCheck={false}
                                          className="w-full text-[12px] leading-relaxed px-3 py-[10px] rounded-[10px] outline-none resize-y"
                                          style={{ background: '#FFFFFF', border: '1px solid #E8EAF6', color: '#0E0B1E' }}
                                        />
                                        <div className="flex items-center gap-2 mt-[10px]">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              navigator.clipboard.writeText(privacyBausteinText ?? buildPrivacyBaustein());
                                              alert('Text kopiert!');
                                            }}
                                            className="inline-flex items-center gap-[6px] text-white text-[12px] font-semibold px-[14px] py-[7px] rounded-[9px]"
                                            style={{ background: '#0E0B1E' }}
                                          >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                                            Text kopieren
                                          </button>
                                          {privacyBausteinText !== null && (
                                            <button
                                              type="button"
                                              onClick={() => setPrivacyBausteinText(null)}
                                              className="text-[11.5px] font-semibold px-[10px] py-[7px] rounded-[9px]"
                                              style={{ color: '#654F6F' }}
                                            >
                                              Zurücksetzen
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                            <div className="flex justify-end">
                              <button type="submit" className="flex items-center gap-[6px] px-[18px] py-[9px] rounded-[10px] text-white text-[13px] font-bold"
                                style={{ background: 'linear-gradient(135deg, #FF6D00, #FF9A00)', boxShadow: '0 4px 12px rgba(255,109,0,0.3)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                <Icons.Check />
                                Speichern
                              </button>
                            </div>
                          </form>
                        ) : (
                          <p className="text-[13px] py-8 text-center" style={{ color: '#654F6F' }}>Lade Konfiguration...</p>
                        )}
                      </div>
                    </div>

                    {/* Widget Snippet */}
                    {userSettings?.api_key && (
                      <div className="bg-white rounded-[18px] border border-[#E8EAF6] overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(14,11,30,0.04)' }}>
                        <div className="px-[22px] py-[18px] pb-[14px] border-b border-[#E8EAF6]">
                          <div className="text-[13px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Widget einbinden</div>
                          <div className="text-[11px] mt-[1px]" style={{ color: '#654F6F' }}>Code-Snippet oder WordPress-Plugin für deine Webseite</div>
                        </div>
                        <div className="p-[22px]">
                          <div
                            className="flex gap-3 rounded-xl px-3.5 py-3 mb-3 border border-[#FF6D00]/22"
                            style={{ background: 'linear-gradient(135deg, rgba(255,109,0,0.07) 0%, rgba(255,154,0,0.05) 100%)' }}
                          >
                            <div className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: 'rgba(255,109,0,0.12)', color: '#CC5500' }}>
                              <Icons.Key />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[12px] font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                Vertraulicher Einbindungscode
                              </div>
                              <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#654F6F' }}>
                                Enthält deinen <span className="font-semibold text-[#0E0B1E]">API-Key</span> — wie ein Passwort behandeln: nicht an Dritte weitergeben, nicht öffentlich posten (z. B. GitHub, Foren, Screenshots) und nur auf deiner eigenen Website verwenden.
                              </p>
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="text-[10px] font-bold uppercase tracking-[0.08em] mb-[7px]" style={{ color: '#654F6F' }}>API-Key</div>
                            <div className="flex items-center gap-2">
                              <div
                                tabIndex={0}
                                role="region"
                                aria-label="API-Key, standardmäßig verschwommen"
                                className="group flex-1 min-w-0 overflow-x-auto px-3 py-[9px] rounded-[10px] outline-none focus-visible:ring-2 focus-visible:ring-[#FF6D00]/40"
                                style={{ background: '#F8F9FE', border: '1px solid #E8EAF6' }}
                              >
                                <code
                                  className="block text-[12px] font-mono whitespace-nowrap select-none transition-[filter] duration-300 ease-out blur-[5px] group-hover:blur-none group-focus-within:blur-none group-active:blur-none motion-reduce:blur-none group-hover:select-text group-focus-within:select-text"
                                  style={{ color: '#0E0B1E' }}
                                >
                                  {userSettings.api_key}
                                </code>
                              </div>
                              <button
                                type="button"
                                onClick={() => copyTextToClipboard(userSettings.api_key, 'API-Key kopiert!')}
                                className="flex-shrink-0 inline-flex items-center gap-[6px] text-white text-[12px] font-semibold px-[14px] py-[7px] rounded-[9px]"
                                style={{ background: '#0E0B1E' }}
                              >
                                <Icons.Copy />
                                Kopieren
                              </button>
                            </div>
                          </div>
                          <div
                            tabIndex={0}
                            role="region"
                            aria-label="Widget-Einbindungscode, standardmäßig verschwommen"
                            className="group relative rounded-xl overflow-hidden mb-3 outline-none focus-visible:ring-2 focus-visible:ring-[#FF6D00]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F8F9FE]"
                          >
                            <div
                              className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center px-4 transition-opacity duration-300 opacity-100 group-hover:opacity-0 group-focus-within:opacity-0 group-active:opacity-0 motion-reduce:opacity-0"
                              aria-hidden="true"
                            >
                              <div
                                className="flex items-center gap-2 rounded-[10px] px-3 py-2 text-[11px] font-semibold text-white/95 shadow-lg"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(30,25,45,0.92) 0%, rgba(20,18,35,0.88) 100%)',
                                  backdropFilter: 'blur(8px)',
                                }}
                              >
                                <Icons.Key />
                                <span className="hidden [@media(hover:hover)]:inline">Maus auf den Code — dann scharf lesbar</span>
                                <span className="[@media(hover:hover)]:hidden">Code antippen — dann scharf lesbar</span>
                              </div>
                            </div>
                            <div className="p-4 overflow-x-auto" style={{ background: '#0E0B1E' }}>
                              <pre
                                className="text-[11px] font-mono leading-[1.7] whitespace-pre select-none transition-[filter] duration-300 ease-out blur-[7px] group-hover:blur-none group-focus-within:blur-none group-active:blur-none motion-reduce:blur-none group-hover:select-text group-focus-within:select-text"
                                style={{ color: '#E8EAF6' }}
                              >
{`<script src="https://candlear.de/chat-widget.js"></script>
<script>
  window.CandlearBookingWidget.init({
    apiKey: "${userSettings.api_key}",
    apiUrl: "https://candlear.de/api/chat",
    position: "${botConfig?.widget_settings?.position || 'bottom-right'}",
    title: "${botConfig?.widget_settings?.title || 'Chat mit uns'}",
    greeting: "${botConfig?.greeting_message || 'Hallo! Wie kann ich helfen?'}"${botConfig?.widget_settings?.privacy_url ? `,\n    privacyUrl: "${botConfig.widget_settings.privacy_url}"` : ''}
  });
</script>`}
                              </pre>
                            </div>
                          </div>
                          <button onClick={() => {
                            const privacyPart = botConfig?.widget_settings?.privacy_url
                              ? `,\n    privacyUrl: "${botConfig.widget_settings.privacy_url}"`
                              : '';
                            const snippet = `<script src="https://candlear.de/chat-widget.js"></script>\n<script>\n  window.CandlearBookingWidget.init({\n    apiKey: "${userSettings.api_key}",\n    apiUrl: "https://candlear.de/api/chat",\n    position: "${botConfig?.widget_settings?.position || 'bottom-right'}",\n    title: "${botConfig?.widget_settings?.title || 'Chat mit uns'}",\n    greeting: "${botConfig?.greeting_message || 'Hallo! Wie kann ich helfen?'}"${privacyPart}\n  });\n</script>`;
                            copyTextToClipboard(snippet, 'Code kopiert!');
                          }}
                            className="inline-flex items-center gap-[6px] text-white text-[12px] font-semibold px-[14px] py-[7px] rounded-[9px]"
                            style={{ background: '#0E0B1E' }}>
                            <Icons.Copy />
                            Code kopieren
                          </button>
                          <div className="border-t border-[#E8EAF6] mt-4 pt-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.1em] mb-[7px]" style={{ color: '#654F6F' }}>WordPress-Plugin</div>
                            <p className="text-[11px] leading-relaxed mb-3" style={{ color: '#654F6F' }}>
                              In WordPress unter <span className="font-semibold text-[#0E0B1E]">Plugins → Neues Plugin hinzufügen → Plugin hochladen</span> installieren und aktivieren, dann unter <span className="font-semibold text-[#0E0B1E]">Einstellungen → Candlear</span> den API-Key von oben eintragen. Kein Code nötig.
                            </p>
                            <a
                              href={WORDPRESS_PLUGIN_ZIP_URL}
                              download
                              className="inline-flex items-center gap-[6px] text-white text-[12px] font-semibold px-[14px] py-[7px] rounded-[9px]"
                              style={{ background: '#0E0B1E' }}
                            >
                              <Icons.Download />
                              Plugin herunterladen
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Phone mockup */}
                  <div className="flex flex-col items-center lg:items-start">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] mb-3" style={{ color: '#654F6F' }}>Live-Vorschau</div>
                    <div style={{ width: '260px', background: '#1a1a2e', borderRadius: '32px', padding: '16px 12px', boxShadow: '0 20px 60px rgba(14,11,30,0.4), 0 0 0 1px rgba(255,255,255,0.05)' }}>
                      <div style={{ background: 'white', borderRadius: '22px', overflow: 'hidden', height: '460px', display: 'flex', flexDirection: 'column' }}>
                        <div className="flex items-center gap-2 px-3.5 py-3" style={{ background: 'linear-gradient(135deg, #FF6D00 0%, #FF9A00 100%)' }}>
                          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          </div>
                          <div>
                            <div className="text-white text-[12px] font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{botConfig?.widget_settings?.title || 'Chat mit uns'}</div>
                            <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.7)' }}>● Online</div>
                          </div>
                        </div>
                        <div className="flex-1 flex flex-col gap-2 p-3" style={{ background: '#F8F9FA', overflowY: 'auto' }}>
                          <div className="self-start max-w-[75%] bg-white text-[11px] px-[10px] py-2 rounded-xl leading-[1.4]" style={{ color: '#374151', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderBottomLeftRadius: '4px' }}>
                            {botConfig?.greeting_message || 'Hallo! Ich helfe dir gerne bei der Terminbuchung 👋'}
                          </div>
                          <div className="self-end max-w-[75%] text-white text-[11px] px-[10px] py-2 rounded-xl leading-[1.4]" style={{ background: 'linear-gradient(135deg, #FF6D00, #FF9A00)', borderBottomRightRadius: '4px' }}>
                            Ich möchte einen Termin buchen
                          </div>
                          <div className="self-start max-w-[75%] bg-white text-[11px] px-[10px] py-2 rounded-xl leading-[1.4]" style={{ color: '#374151', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderBottomLeftRadius: '4px' }}>
                            Super! Für welchen Tag?
                          </div>
                        </div>
                        <div className="flex items-center gap-[6px] px-[10px] py-2 bg-white" style={{ borderTop: '1px solid #E5E7EB' }}>
                          <div className="flex-1 text-[10px] rounded-full px-[10px] py-[6px]" style={{ color: '#9CA3AF', background: '#F3F4F6' }}>Nachricht schreiben...</div>
                          <button className="w-[26px] h-[26px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #FF6D00, #FF9A00)' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* QUICK BOOK MODAL */}
        <AnimatePresence>
          {quickBookOpen && (() => {
            const resources: any[] = fetchedResources;
            const appointmentDurations = userSettings?.settings?.appointment_durations;
            const appointmentTypes: any[] = (appointmentDurations?.mode === 'multiple' && appointmentDurations?.types) ? appointmentDurations.types : [];
            const today = startOfDay(new Date());
            const monthStart = startOfMonth(calendarViewDate);
            const monthEnd = endOfMonth(calendarViewDate);
            const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
            const firstWeekday = (getDay(monthStart) + 6) % 7;
            const weekDayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
            const selectedDate = quickBookForm.date ? new Date(quickBookForm.date + 'T12:00') : null;

            const selectDate = (day: Date) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              setQuickBookForm(prev => {
                const updated = { ...prev, date: dateStr, selectedStart: '', selectedEnd: '', selectedResource: '' };
                fetchSuggestions(updated);
                return updated;
              });
              setQuickBookStep(2);
            };

            const goToStep3 = () => {
              if (!quickBookForm.selectedStart) { setQuickBookError('Bitte wähle einen Slot.'); return; }
              setQuickBookError('');
              setQuickBookStep(3);
            };

            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-[#0E0B1E]/65 backdrop-blur-md" onClick={closeQuickBook} />
                <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 20 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-white w-full max-w-[420px] rounded-3xl shadow-[0_32px_80px_rgba(14,11,30,0.4),0_8px_24px_rgba(14,11,30,0.12)] relative z-10 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
                  {/* Header */}
                  <div className="relative px-6 pt-6 pb-5 bg-[#0E0B1E] overflow-hidden">
                    <div className="absolute top-0 right-0 w-56 h-56 bg-[#FF6D00]/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" />
                    <div className="relative flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        {quickBookStep > 1 && (
                          <button onClick={() => { setQuickBookError(''); setQuickBookStep(s => (s - 1) as 1|2|3); }} className="w-7 h-7 flex items-center justify-center rounded-xl bg-white/8 hover:bg-white/15 text-white/60 hover:text-white transition-colors flex-shrink-0">
                            <Icons.ChevronLeft />
                          </button>
                        )}
                        <div>
                          <h3 className="text-sm font-bold text-white leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            {quickBookStep === 1 ? 'Tag auswählen' : quickBookStep === 2 ? 'Zeitfenster wählen' : 'Kundendaten'}
                          </h3>
                          <p className="text-[#654F6F] text-[11px] mt-0.5">
                            {quickBookStep === 1 ? 'Klicke auf einen verfügbaren Tag' : quickBookStep === 2 ? 'Wähle einen freien Slot' : 'Fast fertig — nur noch Name & Kontakt'}
                          </p>
                        </div>
                      </div>
                      <button onClick={closeQuickBook} className="w-7 h-7 flex items-center justify-center rounded-xl bg-white/8 hover:bg-white/15 text-white/50 hover:text-white transition-colors flex-shrink-0">
                        <Icons.X />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {([1, 2, 3] as const).map(s => (
                        <div key={s} className={cn("h-1 flex-1 rounded-full transition-colors", s <= quickBookStep ? "bg-[#FF6D00]" : "bg-white/12")} />
                      ))}
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {quickBookStep === 1 && (
                      <motion.div key="step1" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                        <div className="px-5 pt-5 pb-2">
                          <div className="flex items-center justify-between mb-4">
                            <button onClick={() => setCalendarViewDate(d => subMonths(d, 1))} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#F8F7FC] text-[#654F6F] hover:text-[#0E0B1E] transition-colors">
                              <Icons.ChevronLeft />
                            </button>
                            <span className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {format(calendarViewDate, 'MMMM yyyy', { locale: de })}
                            </span>
                            <button onClick={() => setCalendarViewDate(d => addMonths(d, 1))} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#F8F7FC] text-[#654F6F] hover:text-[#0E0B1E] transition-colors">
                              <Icons.ChevronRight />
                            </button>
                          </div>
                          <div className="grid grid-cols-7 mb-1">
                            {weekDayLabels.map(d => (
                              <div key={d} className="text-center text-[10px] font-bold text-[#654F6F] py-1.5">{d}</div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7">
                            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`e-${i}`} />)}
                            {daysInMonth.map(day => {
                              const isPast = isBefore(day, today);
                              const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                              const isToday = isSameDay(day, today);
                              return (
                                <button key={day.toISOString()} disabled={isPast} onClick={() => selectDate(day)}
                                  className={cn("relative flex items-center justify-center h-9 rounded-xl text-sm font-medium transition-colors mx-px my-px",
                                    isPast ? "text-gray-300 cursor-not-allowed" :
                                    isSelected ? "bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] text-white shadow-[0_4px_12px_rgba(255,109,0,0.3)]" :
                                    isToday ? "text-[#FF6D00] font-bold ring-1.5 ring-[#FF6D00]/50 hover:bg-[#FFF5F0]" :
                                    "text-[#0E0B1E] hover:bg-[#F8F7FC]")}>
                                  {format(day, 'd')}
                                  {isToday && !isSelected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#FF6D00]" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="px-5 pt-3 pb-5 border-t border-[#E8EAF6] mt-3 space-y-2.5">
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-bold text-[#654F6F] uppercase tracking-widest w-[5.5rem] flex-shrink-0 truncate">Uhrzeit</span>
                            <input
                              type="time"
                              value={quickBookForm.time}
                              onChange={e => updateQuickBookField('time', e.target.value)}
                              className="flex-1 min-w-0 bg-[#F8F7FC] border border-[#E8EAF6] text-[#0E0B1E] text-xs px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/20 focus:border-[#FF6D00] transition-colors"
                            />
                          </div>
                          {resources.length > 0 && (
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] font-bold text-[#654F6F] uppercase tracking-widest w-[5.5rem] flex-shrink-0 truncate">{resourceLabelSingular}</span>
                              <select value={quickBookForm.resource} onChange={e => updateQuickBookField('resource', e.target.value)}
                                className="flex-1 min-w-0 bg-[#F8F7FC] border border-[#E8EAF6] text-[#0E0B1E] text-xs px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/20 focus:border-[#FF6D00] transition-colors cursor-pointer">
                                <option value="">Alle {resourceLabelPlural}</option>
                                {resources.map((r: any) => <option key={r.id} value={r.id}>{r.name || r.id}{typeof r.capacity === 'number' ? ` · ${r.capacity} Pers.` : ''}</option>)}
                              </select>
                            </div>
                          )}
                          {appointmentTypes.length > 0 && (
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] font-bold text-[#654F6F] uppercase tracking-widest w-[5.5rem] flex-shrink-0 truncate">Typ</span>
                              <select value={quickBookForm.appointmentType} onChange={e => updateQuickBookField('appointmentType', e.target.value)}
                                className="flex-1 min-w-0 bg-[#F8F7FC] border border-[#E8EAF6] text-[#0E0B1E] text-xs px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/20 focus:border-[#FF6D00] transition-colors cursor-pointer">
                                <option value="">Standard</option>
                                {appointmentTypes.map((t: any) => <option key={String(t.id)} value={String(t.id)}>{t.name} ({t.duration} Min.)</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {quickBookStep === 2 && (
                      <motion.div key="step2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                        <div className="px-5 pt-5 pb-5 space-y-4">
                          <div className="flex items-center justify-between px-4 py-3 bg-[#F8F7FC] rounded-2xl border border-[#E8EAF6]">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] flex items-center justify-center text-white flex-shrink-0">
                                <Icons.Calendar />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-[#0E0B1E]">{selectedDate ? format(selectedDate, 'EEEE, d. MMMM yyyy', { locale: de }) : '—'}</p>
                                <p className="text-[10px] text-[#654F6F] mt-0.5">
                                  {quickBookForm.time ? `Wunschzeit: ${quickBookForm.time} Uhr` : 'Ausgewählter Tag'}
                                </p>
                                {appointmentTypes.length > 0 && (
                                  <p className="text-[10px] mt-0.5 font-semibold" style={{ color: quickBookForm.appointmentType ? '#FF6D00' : '#9B89A8' }}>
                                    {resolveAppointmentTypeLabel(quickBookForm.appointmentType, appointmentTypes) || 'Kein Typ gewählt'}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button onClick={() => setQuickBookStep(1)} className="text-[10px] font-bold text-[#FF6D00] hover:underline flex-shrink-0 ml-3">Ändern</button>
                          </div>
                          <div className="flex items-center justify-between gap-4 px-4 py-3 bg-[#F8F7FC] rounded-2xl border border-[#E8EAF6]">
                            <div>
                              <p className="text-xs font-semibold text-[#0E0B1E]">Lücken-Optimierung</p>
                              <p className="text-[10px] text-[#654F6F] mt-0.5">{quickBookForm.gapAware ? 'Nur lückenfreie Slots' : 'Alle freien Slots'}</p>
                            </div>
                            <button type="button" onClick={() => updateQuickBookField('gapAware', !quickBookForm.gapAware)}
                              className={cn("relative w-9 h-5 rounded-full flex-shrink-0 transition-colors", quickBookForm.gapAware ? "bg-[#FF6D00]" : "bg-[#E8EAF6]")}>
                              <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.2)] transition-transform", quickBookForm.gapAware ? "translate-x-4" : "translate-x-0.5")} />
                            </button>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-[#654F6F] uppercase tracking-widest mb-3">
                              Verfügbare Zeiten{quickBookLoading && <span className="font-normal ml-1 opacity-60">Lädt…</span>}
                            </p>
                            {quickBookLoading ? (
                              <div className="grid grid-cols-3 gap-2">
                                {[1,2,3,4,5,6].map(i => <div key={i} className="h-[68px] bg-[#F8F7FC] rounded-2xl animate-pulse" />)}
                              </div>
                            ) : quickBookSuggestions.length === 0 ? (
                              <div className="py-7 flex flex-col items-center gap-2.5 text-center">
                                <div className="w-10 h-10 rounded-2xl bg-[#F8F7FC] flex items-center justify-center text-[#654F6F]"><Icons.Calendar /></div>
                                <p className="text-xs text-[#654F6F] font-medium">Keine freien Slots für diesen Tag.</p>
                                <button onClick={() => setQuickBookStep(1)} className="text-xs font-bold text-[#FF6D00] hover:underline">Anderen Tag wählen</button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-3 gap-2">
                                {quickBookSuggestions.map((s: any, index: number) => {
                                  const slotResourceId = s.resource || '';
                                  const isSelected = quickBookForm.selectedStart === s.start && quickBookForm.selectedResource === slotResourceId;
                                  const startDt = new Date(s.start);
                                  const tableLabel = resolveResourceLabel(slotResourceId, resources);
                                  return (
                                    <button key={suggestionSlotKey(s, index)} type="button" onClick={() => selectSuggestion(s)}
                                      className={cn("flex flex-col items-center justify-center py-3 px-1.5 rounded-2xl border transition-colors min-h-[76px]",
                                        isSelected
                                          ? "bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] border-transparent text-white shadow-[0_6px_16px_rgba(255,109,0,0.35)]"
                                          : "bg-[#F8F7FC] border-[#E8EAF6] text-[#0E0B1E] hover:border-[#FF6D00]/40 hover:bg-[#FFF5F0]")}>
                                      <span className="text-sm font-bold">{format(startDt, 'HH:mm')}</span>
                                      {tableLabel && (
                                        <span className={cn("text-[10px] mt-0.5 font-semibold leading-tight text-center truncate max-w-full", isSelected ? "text-white" : "text-[#0E0B1E]")}>{tableLabel}</span>
                                      )}
                                      <span className={cn("text-[10px] mt-0.5 font-medium", isSelected ? "text-orange-100" : "text-[#9B89A8]")}>{format(startDt, 'EE dd.MM.', { locale: de })}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {quickBookError && (
                            <div className="px-4 py-3 rounded-2xl text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-700 flex items-start gap-2">
                              <span className="flex-shrink-0">⚠</span><span>{quickBookError}</span>
                            </div>
                          )}
                          <button onClick={goToStep3} className="w-full py-3 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white rounded-2xl text-sm font-bold shadow-[0_4px_14px_rgba(255,109,0,0.3)] hover:shadow-[0_8px_22px_rgba(255,109,0,0.42)] transition-shadow"
                            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Weiter
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {quickBookStep === 3 && (
                      <motion.div key="step3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}>
                        <div className="px-5 pt-5 pb-5 space-y-4">
                          <div className="px-4 py-3 bg-[#F8F7FC] rounded-2xl border border-[#E8EAF6] flex items-center gap-3">
                            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-[#FF6D00] to-[#FF9A00] flex items-center justify-center text-white flex-shrink-0">
                              <Icons.Calendar />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-[#0E0B1E] truncate">
                                {selectedDate ? format(selectedDate, 'EEE, d. MMM', { locale: de }) : '—'}
                                {quickBookForm.selectedStart && ` · ${format(new Date(quickBookForm.selectedStart), 'HH:mm')} – ${format(new Date(quickBookForm.selectedEnd), 'HH:mm')}`}
                              </p>
                              <p className="text-[10px] text-[#654F6F] mt-0.5 font-medium">{
                                (quickBookForm.selectedResource || quickBookForm.resource)
                                  ? (resolveResourceLabel(quickBookForm.selectedResource || quickBookForm.resource, fetchedResources) || quickBookForm.selectedResource || quickBookForm.resource)
                                  : `Alle ${resourceLabelPlural}`
                              }</p>
                              {appointmentTypes.length > 0 && (
                                <p className="text-[10px] mt-0.5 font-semibold" style={{ color: quickBookForm.appointmentType ? '#FF6D00' : '#9B89A8' }}>
                                  {resolveAppointmentTypeLabel(quickBookForm.appointmentType, appointmentTypes) || 'Kein Typ gewählt'}
                                </p>
                              )}
                            </div>
                            <button onClick={() => setQuickBookStep(2)} className="text-[10px] font-bold text-[#FF6D00] hover:underline flex-shrink-0">Ändern</button>
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-[10px] font-bold text-[#654F6F] uppercase tracking-widest mb-2.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6D00]" />
                              Kundenname <span className="text-[#FF6D00] normal-case">*</span>
                            </label>
                            <input type="text" value={quickBookForm.clientName} onChange={e => setQuickBookForm(prev => ({ ...prev, clientName: e.target.value }))}
                              placeholder="z.B. Max Mustermann" autoFocus
                              className="w-full bg-[#F8F7FC] border border-[#E8EAF6] text-[#0E0B1E] text-sm px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/20 focus:border-[#FF6D00] transition-colors placeholder:text-gray-400" />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-[10px] font-bold text-[#654F6F] uppercase tracking-widest mb-2.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#654F6F]" />
                              Kontakt <span className="opacity-60 font-normal normal-case">(optional)</span>
                            </label>
                            <input type="text" value={quickBookForm.contact} onChange={e => setQuickBookForm(prev => ({ ...prev, contact: e.target.value }))}
                              placeholder="E-Mail oder Telefon"
                              className="w-full bg-[#F8F7FC] border border-[#E8EAF6] text-[#0E0B1E] text-sm px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#FF6D00]/20 focus:border-[#FF6D00] transition-colors placeholder:text-gray-400" />
                          </div>
                          {quickBookError && (
                            <div className="px-4 py-3 rounded-2xl text-xs font-semibold bg-red-50 border border-red-200 text-red-700 flex items-start gap-2">
                              <span className="flex-shrink-0">⚠</span><span>{quickBookError}</span>
                            </div>
                          )}
                          <div className="flex gap-3 pt-1">
                            <button onClick={closeQuickBook} className="px-5 py-3 rounded-2xl text-sm font-semibold text-[#654F6F] hover:bg-[#F8F7FC] transition-colors border border-[#E8EAF6]">
                              Abbrechen
                            </button>
                            <button onClick={handleSubmitQuickBook} disabled={quickBookSubmitting}
                              className="flex-1 py-3 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white rounded-2xl text-sm font-bold shadow-[0_4px_14px_rgba(255,109,0,0.3)] hover:shadow-[0_8px_22px_rgba(255,109,0,0.42)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {quickBookSubmitting ? 'Wird gespeichert…' : 'Termin anlegen'}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            );
          })()}
        </AnimatePresence>

        {/* EVENT DETAIL MODAL */}
        <AnimatePresence>
          {selectedEvent && (() => {
            const selectedTypeLabel = resolveAppointmentTypeLabel(selectedEvent.appointmentTypeId, appointmentTypes);
            return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0" style={{ background: 'rgba(14,11,30,0.55)', backdropFilter: 'blur(8px)' }}
                onClick={() => setSelectedEvent(null)} />
              <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white relative z-10 w-full max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain" style={{ borderRadius: '22px', maxWidth: '400px', boxShadow: '0 24px 64px rgba(14,11,30,0.3)' }}>
                {/* Orange gradient header */}
                <div className="relative overflow-hidden px-[22px] py-5 pb-4" style={{ background: 'linear-gradient(135deg, #FF6D00, #FF9A00)' }}>
                  <div className="absolute -top-[30px] -right-[30px] w-[120px] h-[120px] rounded-full pointer-events-none" style={{ background: 'rgba(255,255,255,0.1)' }} />
                  <button onClick={() => setSelectedEvent(null)}
                    className="absolute top-[14px] right-[14px] w-7 h-7 rounded-[8px] flex items-center justify-center text-white transition-colors"
                    style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <div className="text-white text-[16px] font-bold relative" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {selectedEvent.title.split('(')[0].trim()}
                  </div>
                  <div className="text-[12px] mt-[3px] relative" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {format(selectedEvent.start, 'EEEE, d. MMMM yyyy', { locale: de })} · {format(selectedEvent.start, 'HH:mm')} – {format(selectedEvent.end, 'HH:mm')} Uhr
                  </div>
                </div>
                {/* Body */}
                <div className="px-[22px] py-5">
                  <div>
                    <div className="flex gap-3 items-start py-[10px] border-b border-[#F3F4F6]">
                      <div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,109,0,0.1)', color: '#FF6D00' }}>
                        <Icons.Clock />
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.06em]" style={{ color: '#654F6F' }}>Zeit</div>
                        <div className="text-[13px] font-semibold text-[#0E0B1E] mt-[1px]">
                          {format(selectedEvent.start, 'HH:mm')} – {format(selectedEvent.end, 'HH:mm')} Uhr ({Math.round((selectedEvent.end.getTime() - selectedEvent.start.getTime()) / 60000)} Min.)
                        </div>
                      </div>
                    </div>
                    {selectedTypeLabel && (
                      <div className="flex gap-3 items-start py-[10px] border-b border-[#F3F4F6]">
                        <div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,109,0,0.08)', color: '#FF6D00' }}>
                          <Icons.Zap />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.06em]" style={{ color: '#654F6F' }}>Terminart</div>
                          <div className="text-[13px] font-semibold text-[#0E0B1E] mt-[1px]">{selectedTypeLabel}</div>
                        </div>
                      </div>
                    )}
                    {(selectedEvent.resourceId || selectedEvent.extendedProps?.resourceId) && (
                      <div className="flex gap-3 items-start py-[10px] border-b border-[#F3F4F6]">
                        <div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,176,255,0.1)', color: '#00B0FF' }}>
                          <Icons.Users />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.06em]" style={{ color: '#654F6F' }}>{resourceLabelSingular}</div>
                          <div className="text-[13px] font-semibold text-[#0E0B1E] mt-[1px]">
                            {selectedEvent.extendedProps?.resourceName || selectedEvent.resourceId || selectedEvent.extendedProps?.resourceId}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedEvent.contact && (
                      <div className="flex gap-3 items-start py-[10px] border-b border-[#F3F4F6]">
                        <div className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(101,79,111,0.1)', color: '#654F6F' }}>
                          <Icons.Phone />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.06em]" style={{ color: '#654F6F' }}>Kontakt</div>
                          <div className="text-[13px] font-semibold text-[#0E0B1E] mt-[1px]">{selectedEvent.contact}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex justify-center px-[22px] pb-5 pt-0">
                  <button onClick={async () => {
                    if (!window.confirm('Möchten Sie diesen Termin wirklich stornieren?')) return;
                    try {
                      await axios.delete(`${API_BASE_URL}/admin/events/${selectedEvent.id}`, { headers: await getAuthHeader() });
                      setSelectedEvent(null); void syncAppointments('refresh');
                      alert('Termin erfolgreich storniert');
                    } catch (err: any) { alert(err.response?.data?.error || 'Fehler beim Stornieren'); }
                  }}
                    className="min-w-[160px] py-[9px] px-8 rounded-[10px] text-[12px] font-bold transition-colors hover:bg-red-50"
                    style={{ border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Stornieren
                  </button>
                </div>
              </motion.div>
            </div>
            );
          })()}
        </AnimatePresence>

      </div>
    </div>
  );
}

// --- SUB COMPONENTS ---

function NavButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center w-full px-3 py-[9px] rounded-xl transition-all duration-150 gap-3 relative",
        active
          ? "text-white font-semibold"
          : "text-[#6B5F80] hover:text-white"
      )}
      style={active ? { background: 'rgba(255,109,0,0.10)', borderLeft: '2px solid #FF6D00' } : { borderLeft: '2px solid transparent' }}
    >
      <span style={active ? { color: '#FF6D00' } : {}}>{icon}</span>
      <span className="text-[12px] font-semibold flex-1 text-left">{label}</span>
      {active && <div className="w-[6px] h-[6px] rounded-full bg-[#FF6D00]" />}
    </button>
  );
}

function StatCard({
  title,
  value,
  colorClass,
  icon,
}: {
  title: string;
  value: number;
  colorClass: 'orange' | 'purple' | 'sky' | 'dark';
  icon: React.ReactNode;
}) {
  const styles: Record<string, { bg: string; shadow: string }> = {
    orange: { bg: 'linear-gradient(135deg, #FF6D00, #FF9A00)', shadow: '0 8px 24px rgba(255,109,0,0.24)' },
    purple: { bg: 'linear-gradient(135deg, #654F6F, #8B69A0)', shadow: '0 8px 24px rgba(101,79,111,0.24)' },
    sky: { bg: 'linear-gradient(135deg, #00B0FF, #0076E8)', shadow: '0 8px 24px rgba(0,176,255,0.24)' },
    dark: { bg: 'linear-gradient(135deg, #1A1333, #2D2050)', shadow: '0 8px 24px rgba(14,11,30,0.24)' },
  };
  const s = styles[colorClass];
  return (
    <div className="rounded-[18px] py-4 sm:py-[22px] px-5 text-white relative overflow-hidden cursor-default" style={{ background: s.bg, boxShadow: s.shadow }}>
      <div className="absolute -right-5 -top-5 w-[90px] h-[90px] rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
      <div className="absolute right-4 top-9 w-[46px] h-[46px] rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
      {/* Handy: kompakt in einer Zeile, ab sm wie gehabt gestapelt */}
      <div className="relative flex items-center gap-4 sm:block">
        <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 sm:mb-3" style={{ background: 'rgba(255,255,255,0.18)' }}>
          {icon}
        </div>
        <p className="flex-1 min-w-0 text-[10px] font-bold uppercase tracking-[0.1em] sm:mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{title}</p>
        <p className="text-[28px] sm:text-[36px] font-black text-white leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</p>
      </div>
    </div>
  );
}
